/**
 * Elite Batch 20 Part 5 — Workflow Templates 1928–1947
 * Work anniversary outreach, wrong-person reroute, YouTube transcript lead research,
 * beehiiv subscriber enrichment, audit trail generator, audit trail logger,
 * CCPA consumer request handler, cloud resource tag compliance, compliance questionnaire bot,
 * consent management audit trail, data breach notification, data classification scanner,
 * data retention compliance monitor, data retention policy enforcer, GDPR cookie consent
 * manager, GDPR DSAR bot, HIPAA appointment reminder, IAM access review, import export
 * compliance checker, information security incident logger.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 20 PART 5 — TEMPLATES 1928–1947
// =============================================================================

export const ELITE_BATCH20_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1928. WORK ANNIVERSARY OUTREACH CAMPAIGN (8 nodes)
  // ===================================================================

  "Work Anniversary Outreach Campaign": {
    summary:
      "Runs daily on a schedule, detects work anniversaries from CRM data with runCode, fetches the contact's relationship history from HubSpot, AI-generates personalized anniversary congratulations, branches by relationship depth condition, validates the contact email via API, sends a warm anniversary email, and logs the touchpoint back to HubSpot CRM.",
    nodes: [
      trigger("Schedule: Daily work anniversary detection", "schedule", {
        schedule: "Every day at 7:30 AM",
        howItStarts:
          "Runs daily to detect contacts whose work anniversary falls today, generate personalized congratulations messages, and deliver anniversary outreach to deepen relationships",
        setup:
          "Configure HubSpot token for CRM contact lookups and activity logging. Set OpenAI API key for personalized message generation. Configure ZeroBounce or email validation service for email verification. Set email sending credentials.",
        inputFields: [],
      }),

      // 2. Detect work anniversaries runCode
      n("2", "runCode", "Detect Work Anniversaries from CRM Data", 280, {
        runCodeDetails: {
          code: `const today = new Date();
const currentMonth = today.getMonth() + 1;
const currentDay = today.getDate();
// Simulated CRM anniversary scan — in production this queries HubSpot contacts with start_date property
let anniversaryContacts = [];
const sampleContacts = [
  { email: 'jane.doe@acme.com', name: 'Jane Doe', company: 'Acme Corp', startDate: '2021-' + String(currentMonth).padStart(2,'0') + '-' + String(currentDay).padStart(2,'0'), role: 'VP of Sales' },
  { email: 'john.smith@globex.io', name: 'John Smith', company: 'Globex Inc', startDate: '2019-' + String(currentMonth).padStart(2,'0') + '-' + String(currentDay).padStart(2,'0'), role: 'Director of Engineering' },
];
sampleContacts.forEach(c => {
  const startParts = c.startDate.split('-');
  if (parseInt(startParts[1]) === currentMonth && parseInt(startParts[2]) === currentDay) {
    const years = today.getFullYear() - parseInt(startParts[0]);
    anniversaryContacts.push({ ...c, years_at_company: years });
  }
});
const topContact = anniversaryContacts[0] || {};
output.contact_email = topContact.email || '';
output.contact_name = topContact.name || '';
output.contact_company = topContact.company || '';
output.contact_role = topContact.role || '';
output.years_at_company = topContact.years_at_company || 0;
output.anniversary_count = anniversaryContacts.length;
output.has_anniversaries = anniversaryContacts.length > 0 ? 'yes' : 'no';`,
          outputVariables: [
            { name: "contact_email", type: "string" },
            { name: "contact_name", type: "string" },
            { name: "contact_company", type: "string" },
            { name: "contact_role", type: "string" },
            { name: "years_at_company", type: "number" },
            { name: "anniversary_count", type: "number" },
            { name: "has_anniversaries", type: "string" },
          ],
        },
      }),

      // 3. Fetch contact relationship history HubSpot
      hubspotGetNode(
        "3",
        "Fetch Contact Relationship History from HubSpot",
        520,
        "crm/v3/objects/contacts/{contact_email}?properties=notes_last_contacted,num_contacted_notes,hs_email_last_open_date,lifecyclestage,hubspot_owner_id,hs_lead_status",
        [
          {
            name: "properties.notes_last_contacted",
            customName: "last_contacted_date",
            type: "string",
          },
          {
            name: "properties.num_contacted_notes",
            customName: "total_interactions",
            type: "string",
          },
          {
            name: "properties.hs_email_last_open_date",
            customName: "last_email_open",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "contact_owner_id",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 4. AI anniversary congratulations
      aiNode(
        "4",
        "AI Generate Anniversary Congratulations",
        760,
        "You are an expert relationship manager. Generate a warm, personalized work anniversary congratulations message. Reference the contact's role, tenure, company, and relationship depth. Adjust tone based on how deep the relationship is — long-time contacts get more personal messages, newer contacts get professional but warm messages. Return JSON: {subject_line, anniversary_message, relationship_depth, personalization_notes, suggested_gift_idea, follow_up_action}",
        "Contact: {contact_name} at {contact_company} | Role: {contact_role} | Years: {years_at_company} | Last contacted: {last_contacted_date} | Total interactions: {total_interactions} | Last email open: {last_email_open} | Lifecycle: {lifecycle_stage} | Lead status: {lead_status}",
        [
          { field: "subject_line", as: "anniversary_subject", type: "string" },
          {
            field: "anniversary_message",
            as: "anniversary_message",
            type: "string",
          },
          {
            field: "relationship_depth",
            as: "relationship_depth",
            type: "string",
          },
          {
            field: "personalization_notes",
            as: "personalization_notes",
            type: "string",
          },
          {
            field: "suggested_gift_idea",
            as: "suggested_gift",
            type: "string",
          },
          { field: "follow_up_action", as: "follow_up_action", type: "string" },
        ],
      ),

      // 5. Branch by relationship depth condition
      n(
        "5",
        "condition",
        "Is relationship depth deep enough for personalized outreach?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "relationship_depth",
                operator: "equals",
                value: "deep",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Validate contact email API (output_1 = deep relationship)
      n("6", "hitApi", "Validate Contact Email via ZeroBounce", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "email_valid_status",
              type: "string",
            },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Send anniversary email
      emailNode(
        "7",
        "Send Anniversary Congratulations Email",
        1480,
        "{contact_email}",
        "{anniversary_subject}",
        "{anniversary_message}",
      ),

      // 8. Log touchpoint CRM HubSpot (output_2 = shallow relationship, just log)
      hubspotPostNode(
        "8",
        "Log Anniversary Touchpoint in HubSpot CRM",
        1240,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"Work Anniversary Outreach: {contact_name} at {contact_company} — {years_at_company} year(s). Relationship depth: {relationship_depth}. Gift suggestion: {suggested_gift}. Follow-up: {follow_up_action}.","hs_timestamp":"{now}"}',
          },
          {
            key: "associations",
            value:
              '[{"to":{"id":"{contact_owner_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":202}]}]',
          },
        ],
        [
          { name: "id", customName: "note_id", type: "string" },
          {
            name: "properties.hs_note_body",
            customName: "note_body",
            type: "string",
          },
        ],
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
      e("5", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1929. WRONG-PERSON REROUTE (8 nodes)
  // ===================================================================

  "Wrong-Person Reroute": {
    summary:
      "Fires when a reply is detected via webhook, AI-classifies whether it's a wrong-person reply, AI-extracts referral information from the reply, finds the referred contact via API, AI-generates rerouted outreach, branches by referral quality condition, sends the rerouted outreach email, and updates CRM records in HubSpot.",
    nodes: [
      trigger("Webhook: Email reply detected", "webhook_app", {
        source: "sendgrid",
        event: "email.reply",
        howItStarts:
          "Fires when an email reply is received via SendGrid inbound parse, sending the raw reply event with sender, subject, body, and original message metadata",
        setup:
          "Configure SendGrid inbound parse webhook for email replies. Set OpenAI API key for reply classification and outreach generation. Configure Apollo API for contact discovery. Set HubSpot token for CRM updates.",
        inputFields: [],
      }),

      // 2. AI classify wrong-person reply
      aiNode(
        "2",
        "AI Classify Wrong-Person Reply",
        280,
        "You are an email reply analyst. Analyze the incoming reply to determine if it is a wrong-person response (e.g., 'I'm not the right person', 'You should talk to...', 'Try reaching out to...', 'I no longer handle this'). Extract the original recipient, the reply sender, and whether a referral was provided. Return JSON: {is_wrong_person, original_recipient_email, reply_sender_email, reply_sender_name, reply_sender_company, referral_provided, referral_name, referral_title, referral_email_hint, referral_department, original_subject, reply_body_summary}",
        "Raw email reply webhook: {webhook_payload}. Classify whether this is a wrong-person reply and extract any referral information regardless of how it is worded.",
        [
          { field: "is_wrong_person", as: "is_wrong_person", type: "string" },
          {
            field: "original_recipient_email",
            as: "original_recipient_email",
            type: "string",
          },
          {
            field: "reply_sender_email",
            as: "reply_sender_email",
            type: "string",
          },
          {
            field: "reply_sender_name",
            as: "reply_sender_name",
            type: "string",
          },
          {
            field: "reply_sender_company",
            as: "reply_sender_company",
            type: "string",
          },
          {
            field: "referral_provided",
            as: "referral_provided",
            type: "string",
          },
          { field: "referral_name", as: "referral_name", type: "string" },
          { field: "referral_title", as: "referral_title", type: "string" },
          {
            field: "referral_email_hint",
            as: "referral_email_hint",
            type: "string",
          },
          {
            field: "referral_department",
            as: "referral_department",
            type: "string",
          },
          { field: "original_subject", as: "original_subject", type: "string" },
          {
            field: "reply_body_summary",
            as: "reply_body_summary",
            type: "string",
          },
        ],
      ),

      // 3. AI extract referral information
      aiNode(
        "3",
        "AI Extract Referral Context and Intent",
        520,
        "You are a sales referral analyst. Given a wrong-person reply with referral information, determine the quality of the referral, the likelihood of a warm connection, and how to reference the referral in new outreach. Score referral quality as high, medium, or low. Return JSON: {referral_quality, warm_intro_likelihood, referral_context, recommended_approach, opening_line, value_prop_angle, urgency_level}",
        "Wrong-person reply from: {reply_sender_name} ({reply_sender_email}) at {reply_sender_company}. Referred to: {referral_name} ({referral_title}) in {referral_department}. Email hint: {referral_email_hint}. Original subject: {original_subject}. Reply summary: {reply_body_summary}",
        [
          { field: "referral_quality", as: "referral_quality", type: "string" },
          {
            field: "warm_intro_likelihood",
            as: "warm_intro_likelihood",
            type: "string",
          },
          { field: "referral_context", as: "referral_context", type: "string" },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
          { field: "opening_line", as: "opening_line", type: "string" },
          { field: "value_prop_angle", as: "value_prop_angle", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 4. Find referred contact API
      n("4", "hitApi", "Find Referred Contact via Apollo", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_name", value: "{reply_sender_company}" },
            { key: "person_titles", value: '["{referral_title}"]' },
            { key: "person_name", value: "{referral_name}" },
            { key: "per_page", value: "3" },
          ],
          variables: [
            {
              name: "people[0].email",
              customName: "referred_contact_email",
              type: "string",
            },
            {
              name: "people[0].first_name",
              customName: "referred_first_name",
              type: "string",
            },
            {
              name: "people[0].last_name",
              customName: "referred_last_name",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "referred_actual_title",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "referred_linkedin",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI rerouted outreach
      aiNode(
        "5",
        "AI Generate Rerouted Outreach Message",
        1000,
        "You are an expert sales copywriter. Generate a rerouted outreach email to the referred contact. Mention that their colleague referred you, use the appropriate value prop angle, and keep the tone warm and professional. Return JSON: {rerouted_subject, rerouted_body, ps_line, call_to_action}",
        "Referred by: {reply_sender_name} at {reply_sender_company}. New contact: {referred_first_name} {referred_last_name} ({referred_actual_title}). Referral context: {referral_context}. Opening line: {opening_line}. Value prop: {value_prop_angle}. Approach: {recommended_approach}. Original subject: {original_subject}.",
        [
          { field: "rerouted_subject", as: "rerouted_subject", type: "string" },
          { field: "rerouted_body", as: "rerouted_body", type: "string" },
          { field: "ps_line", as: "ps_line", type: "string" },
          { field: "call_to_action", as: "reroute_cta", type: "string" },
        ],
      ),

      // 6. Branch by referral quality condition
      n(
        "6",
        "condition",
        "Is referral quality high enough for immediate outreach?",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "referral_quality",
                operator: "equals",
                value: "high",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Send rerouted outreach email (output_1 = high quality)
      emailNode(
        "7",
        "Send Rerouted Outreach Email",
        1480,
        "{referred_contact_email}",
        "{rerouted_subject}",
        "{rerouted_body}\\n\\nP.S. {ps_line}",
      ),

      // 8. Update CRM records HubSpot (output_2 = lower quality, just log)
      hubspotPostNode(
        "8",
        "Update CRM Reroute Records in HubSpot",
        1480,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"Wrong-Person Reroute: Original recipient {original_recipient_email} referred to {referred_first_name} {referred_last_name} ({referred_actual_title}) at {reply_sender_company}. Referral quality: {referral_quality}. Warm intro likelihood: {warm_intro_likelihood}. Context: {referral_context}. Urgency: {urgency_level}.","hs_timestamp":"{now}"}',
          },
        ],
        [{ name: "id", customName: "reroute_note_id", type: "string" }],
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1930. YOUTUBE TRANSCRIPT TO LEAD RESEARCH (9 nodes)
  // ===================================================================

  "YouTube Transcript to Lead Research": {
    summary:
      "Fires when a video is nominated via webhook, fetches the YouTube video transcript via API, AI-extracts company mentions and pain points, researches mentioned companies via Clearbit, finds decision-maker contacts via Apollo, scores lead quality with runCode, AI-generates transcript-aware outreach, and creates lead records in HubSpot with Slack notification.",
    nodes: [
      trigger("Webhook: YouTube video nominated for research", "webhook_app", {
        source: "internal",
        event: "video.nominated",
        howItStarts:
          "Fires when a team member nominates a YouTube video for lead research, sending the raw nomination event with video URL, nominator details, and research context",
        setup:
          "Configure internal webhook for video nomination events. Set YouTube Data API key for transcript retrieval. Set OpenAI API key for content analysis and outreach generation. Configure Clearbit and Apollo API keys for company and contact enrichment. Set HubSpot token and Slack webhook.",
        inputFields: [],
      }),

      // 2. AI extract video details from webhook
      aiNode(
        "2",
        "AI Extract Video Nomination Details",
        280,
        "You are a webhook event parser. Extract the YouTube video URL, video ID, nominator email, nominator name, research focus area, target industry, and any notes from the raw nomination webhook. Return JSON: {video_url, video_id, nominator_email, nominator_name, research_focus, target_industry, nomination_notes}",
        "Raw video nomination webhook: {webhook_payload}. Extract all nomination details regardless of field naming conventions.",
        [
          { field: "video_url", as: "video_url", type: "string" },
          { field: "video_id", as: "video_id", type: "string" },
          { field: "nominator_email", as: "nominator_email", type: "string" },
          { field: "nominator_name", as: "nominator_name", type: "string" },
          { field: "research_focus", as: "research_focus", type: "string" },
          { field: "target_industry", as: "target_industry", type: "string" },
          { field: "nomination_notes", as: "nomination_notes", type: "string" },
        ],
      ),

      // 3. Fetch video transcript API
      n("3", "hitApi", "Fetch YouTube Video Transcript", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/youtube/v3/captions?videoId={video_id}&key={youtube_api_key}&part=snippet",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "items[0].snippet.language",
              customName: "transcript_language",
              type: "string",
            },
            { name: "items[0].id", customName: "caption_id", type: "string" },
            {
              name: "items[0].snippet.name",
              customName: "caption_name",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI extract company mentions and pain points
      aiNode(
        "4",
        "AI Extract Company Mentions and Pain Points",
        760,
        "You are a video content analyst specializing in B2B intelligence. Analyze the video transcript to identify company names mentioned, pain points discussed, use cases described, tools referenced, budget indicators, and buying signals. Focus on the research area specified. Return JSON: {companies_mentioned, pain_points, use_cases, tools_referenced, budget_indicators, buying_signals, key_quotes, industry_context, top_company_name, top_company_domain, top_pain_point}",
        "Video transcript from caption: {caption_name} (language: {transcript_language}). Research focus: {research_focus}. Target industry: {target_industry}. Notes: {nomination_notes}. Analyze the video content for B2B lead intelligence.",
        [
          {
            field: "companies_mentioned",
            as: "companies_mentioned",
            type: "string",
          },
          { field: "pain_points", as: "pain_points", type: "string" },
          { field: "use_cases", as: "use_cases", type: "string" },
          { field: "buying_signals", as: "buying_signals", type: "string" },
          { field: "key_quotes", as: "key_quotes", type: "string" },
          { field: "top_company_name", as: "top_company_name", type: "string" },
          {
            field: "top_company_domain",
            as: "top_company_domain",
            type: "string",
          },
          { field: "top_pain_point", as: "top_pain_point", type: "string" },
        ],
      ),

      // 5. Research mentioned companies Clearbit API
      n("5", "hitApi", "Enrich Top Company via Clearbit", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={top_company_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            { name: "tech", customName: "company_tech_stack", type: "string" },
            { name: "geo.city", customName: "company_city", type: "string" },
          ],
        },
      }),

      // 6. Find decision-maker contacts API
      n("6", "hitApi", "Find Decision-Maker Contacts via Apollo", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_name", value: "{enriched_company_name}" },
            {
              key: "person_titles",
              value: '["VP","Director","Head of","Manager"]',
            },
            { key: "per_page", value: "5" },
          ],
          variables: [
            { name: "people[0].email", customName: "dm_email", type: "string" },
            {
              name: "people[0].first_name",
              customName: "dm_first_name",
              type: "string",
            },
            {
              name: "people[0].last_name",
              customName: "dm_last_name",
              type: "string",
            },
            { name: "people[0].title", customName: "dm_title", type: "string" },
            {
              name: "people[0].linkedin_url",
              customName: "dm_linkedin",
              type: "string",
            },
          ],
        },
      }),

      // 7. Score lead quality runCode
      n(
        "7",
        "runCode",
        "Score Lead Quality from Transcript Intelligence",
        1480,
        {
          runCodeDetails: {
            code: `let score = 0;
const employees = parseInt(variables.company_employees) || 0;
const revenue = (variables.company_revenue || '').toLowerCase();
const buyingSignals = (variables.buying_signals || '').toLowerCase();
const painPoints = (variables.pain_points || '').toLowerCase();
if (employees >= 500) score += 25;
else if (employees >= 100) score += 15;
else if (employees >= 20) score += 10;
if (revenue.includes('$10m') || revenue.includes('$50m') || revenue.includes('$100m')) score += 20;
else if (revenue.includes('$1m') || revenue.includes('$5m')) score += 10;
if (buyingSignals.includes('budget') || buyingSignals.includes('evaluating') || buyingSignals.includes('looking for')) score += 20;
if (buyingSignals.includes('timeline') || buyingSignals.includes('this quarter')) score += 15;
if (painPoints.length > 50) score += 10;
if (variables.dm_email && variables.dm_email.includes('@')) score += 10;
output.lead_score = score;
output.lead_tier = score >= 70 ? 'hot' : score >= 40 ? 'warm' : 'nurture';
output.score_breakdown = 'Employees: ' + employees + ', Revenue signal: ' + (revenue || 'unknown') + ', Buying signals: ' + (buyingSignals.length > 0 ? 'present' : 'none') + ', DM found: ' + (variables.dm_email ? 'yes' : 'no');`,
            outputVariables: [
              { name: "lead_score", type: "number" },
              { name: "lead_tier", type: "string" },
              { name: "score_breakdown", type: "string" },
            ],
          },
        },
      ),

      // 8. AI transcript-aware outreach
      aiNode(
        "8",
        "AI Generate Transcript-Aware Outreach",
        1720,
        "You are an expert B2B sales copywriter. Generate a highly personalized outreach email that references specific pain points from the video transcript. Mention the video content naturally without being creepy. Use the lead score to calibrate urgency. Return JSON: {outreach_subject, outreach_body, video_reference_hook, pain_point_mention, call_to_action}",
        "Contact: {dm_first_name} {dm_last_name} ({dm_title}) at {enriched_company_name}. Industry: {company_industry}. Employees: {company_employees}. Pain points from video: {top_pain_point}. Use cases: {use_cases}. Key quotes: {key_quotes}. Lead tier: {lead_tier} (score: {lead_score}). Research focus: {research_focus}.",
        [
          { field: "outreach_subject", as: "outreach_subject", type: "string" },
          { field: "outreach_body", as: "outreach_body", type: "string" },
          {
            field: "video_reference_hook",
            as: "video_reference_hook",
            type: "string",
          },
          { field: "call_to_action", as: "outreach_cta", type: "string" },
        ],
      ),

      // 9. Create lead records notify HubSpot Slack
      slackNode(
        "9",
        "Create Lead Record and Notify Team",
        1960,
        "YouTube Transcript Lead Research Complete\\n\\n*Video:* {video_url}\\n*Company:* {enriched_company_name} ({company_industry})\\n*Employees:* {company_employees} | *Revenue:* {company_revenue}\\n*Decision Maker:* {dm_first_name} {dm_last_name} — {dm_title}\\n*Email:* {dm_email}\\n\\n*Lead Score:* {lead_score} ({lead_tier})\\n*Pain Point:* {top_pain_point}\\n*Buying Signals:* {buying_signals}\\n*Outreach Subject:* {outreach_subject}\\n*Video Hook:* {video_reference_hook}\\n\\nNominated by {nominator_name}. Review and send outreach.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1931. BEEHIIV SUBSCRIBER ENRICHMENT (8 nodes)
  // ===================================================================

  "beehiiv Subscriber Enrichment": {
    summary:
      "Fires when a new subscriber is added via beehiiv webhook, extracts subscriber details with runCode, enriches with company data via Clearbit, AI-scores sales potential, calculates a subscriber value score with runCode, branches by value score condition, syncs high-value subscribers to HubSpot CRM, and notifies sales via Slack.",
    nodes: [
      trigger("Webhook: New beehiiv subscriber", "webhook_app", {
        source: "beehiiv",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber joins the beehiiv newsletter, sending the raw subscription event with email, referral source, UTM parameters, and signup metadata",
        setup:
          "Configure beehiiv webhook for subscriber.created events. Set OpenAI API key for sales potential scoring. Configure Clearbit API key for company enrichment. Set HubSpot token for CRM sync. Set Slack webhook for sales notifications.",
        inputFields: [],
      }),

      // 2. Fetch subscriber details runCode
      n("2", "runCode", "Parse Subscriber Details from Webhook", 280, {
        runCodeDetails: {
          code: `const payload = variables.webhook_payload || '{}';
let parsed = {};
try { parsed = typeof payload === 'string' ? JSON.parse(payload) : payload; } catch(e) { parsed = {}; }
const data = parsed.data || parsed.subscriber || parsed;
output.subscriber_email = data.email || data.subscriber_email || '';
output.subscriber_name = data.name || data.full_name || data.first_name || '';
output.referral_source = data.referral_source || data.utm_source || data.source || 'direct';
output.utm_medium = data.utm_medium || '';
output.utm_campaign = data.utm_campaign || '';
output.subscription_tier = data.tier || data.subscription_type || 'free';
output.subscriber_id = data.id || data.subscriber_id || '';
const emailDomain = (output.subscriber_email || '').split('@')[1] || '';
const freeEmailDomains = ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com','protonmail.com'];
output.email_domain = emailDomain;
output.is_business_email = freeEmailDomains.includes(emailDomain.toLowerCase()) ? 'no' : 'yes';`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "subscriber_name", type: "string" },
            { name: "referral_source", type: "string" },
            { name: "utm_medium", type: "string" },
            { name: "utm_campaign", type: "string" },
            { name: "subscription_tier", type: "string" },
            { name: "subscriber_id", type: "string" },
            { name: "email_domain", type: "string" },
            { name: "is_business_email", type: "string" },
          ],
        },
      }),

      // 3. Enrich with company data Clearbit API
      n("3", "hitApi", "Enrich Subscriber Company via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={email_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            {
              name: "metrics.employees",
              customName: "company_size",
              type: "string",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "category.subIndustry",
              customName: "company_sub_industry",
              type: "string",
            },
            { name: "tech", customName: "company_tech_stack", type: "string" },
          ],
        },
      }),

      // 4. AI score sales potential
      aiNode(
        "4",
        "AI Score Subscriber Sales Potential",
        760,
        "You are a B2B lead scoring specialist. Evaluate this newsletter subscriber for sales potential based on their company data, email type, referral source, and subscription tier. Score from 0-100 and provide a recommended action. Return JSON: {sales_potential_score, icp_fit, recommended_action, engagement_prediction, conversion_likelihood, persona_match, outreach_timing}",
        "Subscriber: {subscriber_name} ({subscriber_email}). Company: {company_name} | Industry: {company_industry} | Employees: {company_size} | Revenue: {company_revenue} | Tech: {company_tech_stack}. Business email: {is_business_email}. Referral: {referral_source}. UTM: {utm_medium}/{utm_campaign}. Tier: {subscription_tier}.",
        [
          {
            field: "sales_potential_score",
            as: "sales_potential_score",
            type: "string",
          },
          { field: "icp_fit", as: "icp_fit", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "engagement_prediction",
            as: "engagement_prediction",
            type: "string",
          },
          {
            field: "conversion_likelihood",
            as: "conversion_likelihood",
            type: "string",
          },
          { field: "persona_match", as: "persona_match", type: "string" },
          { field: "outreach_timing", as: "outreach_timing", type: "string" },
        ],
      ),

      // 5. Calculate subscriber value score runCode
      n("5", "runCode", "Calculate Composite Subscriber Value Score", 1000, {
        runCodeDetails: {
          code: `const aiScore = parseInt(variables.sales_potential_score) || 0;
const companySize = parseInt(variables.company_size) || 0;
const isBusinessEmail = variables.is_business_email === 'yes';
const icpFit = (variables.icp_fit || '').toLowerCase();
let valueScore = aiScore;
if (isBusinessEmail) valueScore += 10;
if (companySize >= 500) valueScore += 15;
else if (companySize >= 100) valueScore += 10;
else if (companySize >= 20) valueScore += 5;
if (icpFit.includes('strong') || icpFit.includes('excellent')) valueScore += 15;
else if (icpFit.includes('moderate') || icpFit.includes('good')) valueScore += 8;
if ((variables.referral_source || '').includes('partner') || (variables.referral_source || '').includes('referral')) valueScore += 10;
valueScore = Math.min(valueScore, 100);
output.composite_value_score = valueScore;
output.value_tier = valueScore >= 70 ? 'high_value' : valueScore >= 40 ? 'medium_value' : 'low_value';
output.auto_sync_crm = valueScore >= 70 ? 'yes' : 'no';
output.score_summary = 'AI score: ' + aiScore + ', Company size bonus: ' + (companySize >= 500 ? '+15' : companySize >= 100 ? '+10' : companySize >= 20 ? '+5' : '+0') + ', Business email: ' + (isBusinessEmail ? '+10' : '+0') + ', ICP fit: ' + icpFit;`,
          outputVariables: [
            { name: "composite_value_score", type: "number" },
            { name: "value_tier", type: "string" },
            { name: "auto_sync_crm", type: "string" },
            { name: "score_summary", type: "string" },
          ],
        },
      }),

      // 6. Branch by value score condition
      n("6", "condition", "Is subscriber high-value for CRM sync?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "auto_sync_crm", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Sync high-value to CRM HubSpot (output_1 = high value)
      hubspotPostNode(
        "7",
        "Sync High-Value Subscriber to HubSpot CRM",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{subscriber_email}","firstname":"{subscriber_name}","company":"{company_name}","industry":"{company_industry}","numberofemployees":"{company_size}","annualrevenue":"{company_revenue}","hs_lead_status":"NEW","lifecyclestage":"lead","notes_last_contacted":"beehiiv subscriber — value score: {composite_value_score} ({value_tier}). ICP fit: {icp_fit}. Action: {recommended_action}. Timing: {outreach_timing}."}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "synced_email",
            type: "string",
          },
        ],
      ),

      // 8. Notify sales Slack (output_2 = medium/low value, still notify)
      slackNode(
        "8",
        "Notify Sales of New Subscriber Intelligence",
        1480,
        "beehiiv Subscriber Enrichment Complete\\n\\n*Subscriber:* {subscriber_name} ({subscriber_email})\\n*Company:* {company_name} ({company_industry})\\n*Size:* {company_size} employees | *Revenue:* {company_revenue}\\n*Value Score:* {composite_value_score}/100 ({value_tier})\\n*ICP Fit:* {icp_fit} | *Persona:* {persona_match}\\n*Referral Source:* {referral_source}\\n*Recommended Action:* {recommended_action}\\n*Outreach Timing:* {outreach_timing}\\n*Score Breakdown:* {score_summary}",
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1932. AUDIT TRAIL GENERATOR (8 nodes)
  // ===================================================================

  "Audit Trail Generator": {
    summary:
      "Fires when an audit request is received via webhook, collects event metadata with runCode, enriches with actor context using runCode, AI-generates an audit narrative, AI-customizes tone for the compliance framework, branches by risk level condition, stores the audit record in Google Sheets, and notifies the compliance team via Slack.",
    nodes: [
      trigger("Webhook: Audit request received", "webhook_app", {
        source: "internal",
        event: "audit.request",
        howItStarts:
          "Fires when an audit request is submitted internally, sending the raw audit request event with action type, actor, resource, timestamp, and compliance framework reference",
        setup:
          "Configure internal webhook for audit request events. Set OpenAI API key for narrative generation and tone customization. Configure Google Sheets for audit record storage. Set Slack webhook for compliance team notifications.",
        inputFields: [],
      }),

      // 2. Collect event metadata runCode
      n("2", "runCode", "Collect and Normalize Audit Event Metadata", 280, {
        runCodeDetails: {
          code: `const payload = variables.webhook_payload || '{}';
let parsed = {};
try { parsed = typeof payload === 'string' ? JSON.parse(payload) : payload; } catch(e) { parsed = {}; }
const event = parsed.event || parsed.audit_event || parsed;
output.event_type = event.type || event.action || event.event_type || 'unknown';
output.actor_id = event.actor_id || event.user_id || event.performed_by || '';
output.actor_email = event.actor_email || event.user_email || '';
output.resource_type = event.resource_type || event.object_type || '';
output.resource_id = event.resource_id || event.object_id || '';
output.action_performed = event.action || event.operation || event.event_type || '';
output.ip_address = event.ip_address || event.source_ip || '';
output.user_agent = event.user_agent || '';
output.timestamp = event.timestamp || new Date().toISOString();
output.compliance_framework = event.compliance_framework || event.framework || 'SOC2';
output.metadata_json = JSON.stringify({ event_type: output.event_type, actor: output.actor_email, resource: output.resource_type + '/' + output.resource_id, ip: output.ip_address });`,
          outputVariables: [
            { name: "event_type", type: "string" },
            { name: "actor_id", type: "string" },
            { name: "actor_email", type: "string" },
            { name: "resource_type", type: "string" },
            { name: "resource_id", type: "string" },
            { name: "action_performed", type: "string" },
            { name: "ip_address", type: "string" },
            { name: "user_agent", type: "string" },
            { name: "timestamp", type: "string" },
            { name: "compliance_framework", type: "string" },
            { name: "metadata_json", type: "string" },
          ],
        },
      }),

      // 3. Enrich with actor context runCode
      n("3", "runCode", "Enrich Audit Event with Actor Context", 520, {
        runCodeDetails: {
          code: `const actorEmail = (variables.actor_email || '').toLowerCase();
const eventType = (variables.event_type || '').toLowerCase();
const resourceType = (variables.resource_type || '').toLowerCase();
let actorRole = 'standard_user';
let riskLevel = 'low';
let accessLevel = 'read';
if (actorEmail.includes('admin') || actorEmail.includes('root')) {
  actorRole = 'administrator';
  riskLevel = 'medium';
}
if (eventType.includes('delete') || eventType.includes('destroy') || eventType.includes('purge')) {
  riskLevel = 'high';
  accessLevel = 'destructive';
} else if (eventType.includes('update') || eventType.includes('modify') || eventType.includes('write')) {
  accessLevel = 'write';
  if (riskLevel === 'low') riskLevel = 'medium';
} else if (eventType.includes('export') || eventType.includes('download')) {
  accessLevel = 'export';
  riskLevel = 'medium';
}
if (resourceType.includes('pii') || resourceType.includes('financial') || resourceType.includes('credential')) {
  riskLevel = 'high';
}
const ipSuspicious = (variables.ip_address || '').startsWith('10.') ? false : true;
if (ipSuspicious && riskLevel === 'medium') riskLevel = 'high';
output.actor_role = actorRole;
output.risk_level = riskLevel;
output.access_level = accessLevel;
output.ip_suspicious = ipSuspicious ? 'yes' : 'no';
output.enrichment_summary = 'Actor: ' + actorRole + ', Risk: ' + riskLevel + ', Access: ' + accessLevel + ', External IP: ' + (ipSuspicious ? 'yes' : 'no');`,
          outputVariables: [
            { name: "actor_role", type: "string" },
            { name: "risk_level", type: "string" },
            { name: "access_level", type: "string" },
            { name: "ip_suspicious", type: "string" },
            { name: "enrichment_summary", type: "string" },
          ],
        },
      }),

      // 4. AI audit narrative
      aiNode(
        "4",
        "AI Generate Audit Narrative",
        760,
        "You are a compliance audit writer. Generate a clear, professional audit trail narrative that describes what happened, who performed the action, what resource was affected, the risk assessment, and recommended follow-up actions. Return JSON: {audit_narrative, executive_summary, risk_assessment, recommended_actions, compliance_impact, retention_period}",
        "Event: {event_type} | Actor: {actor_email} ({actor_role}) | Resource: {resource_type}/{resource_id} | Action: {action_performed} | Access level: {access_level} | Risk: {risk_level} | IP: {ip_address} (suspicious: {ip_suspicious}) | Framework: {compliance_framework} | Timestamp: {timestamp}",
        [
          { field: "audit_narrative", as: "audit_narrative", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "compliance_impact",
            as: "compliance_impact",
            type: "string",
          },
          { field: "retention_period", as: "retention_period", type: "string" },
        ],
      ),

      // 5. AI customize tone compliance framework
      aiNode(
        "5",
        "AI Customize Audit Tone for Compliance Framework",
        1000,
        "You are a compliance framework specialist. Reformat the audit narrative to match the specific compliance framework requirements (SOC2, ISO27001, HIPAA, GDPR, etc.). Add framework-specific control references, evidence tags, and regulatory citations. Return JSON: {formatted_narrative, control_references, evidence_tags, regulatory_citations, framework_section, audit_classification}",
        "Framework: {compliance_framework}. Original narrative: {audit_narrative}. Risk: {risk_assessment}. Actions: {recommended_actions}. Impact: {compliance_impact}. Retention: {retention_period}. Reformat to match the specified compliance framework standards.",
        [
          {
            field: "formatted_narrative",
            as: "formatted_narrative",
            type: "string",
          },
          {
            field: "control_references",
            as: "control_references",
            type: "string",
          },
          { field: "evidence_tags", as: "evidence_tags", type: "string" },
          {
            field: "regulatory_citations",
            as: "regulatory_citations",
            type: "string",
          },
          {
            field: "framework_section",
            as: "framework_section",
            type: "string",
          },
          {
            field: "audit_classification",
            as: "audit_classification",
            type: "string",
          },
        ],
      ),

      // 6. Branch by risk level condition
      n(
        "6",
        "condition",
        "Is risk level high enough for immediate alert?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "risk_level", operator: "equals", value: "high" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Store audit record Sheets
      sheetsNode(
        "7",
        "Store Audit Record in Compliance Log",
        1480,
        "AuditTrail",
        `[["${new Date().toISOString().split("T")[0]}", "{timestamp}", "{event_type}", "{actor_email}", "{actor_role}", "{resource_type}", "{resource_id}", "{action_performed}", "{risk_level}", "{access_level}", "{compliance_framework}", "{framework_section}", "{control_references}", "{evidence_tags}", "{formatted_narrative}"]]`,
      ),

      // 8. Notify compliance team Slack (output_1 = high risk gets urgent alert)
      slackNode(
        "8",
        "Alert Compliance Team of High-Risk Audit Event",
        1480,
        "HIGH-RISK AUDIT EVENT\\n\\n*Event:* {event_type} | *Actor:* {actor_email} ({actor_role})\\n*Resource:* {resource_type}/{resource_id}\\n*Risk Level:* {risk_level} | *Access:* {access_level}\\n*IP:* {ip_address} (suspicious: {ip_suspicious})\\n\\n*Framework:* {compliance_framework} — {framework_section}\\n*Controls:* {control_references}\\n*Evidence Tags:* {evidence_tags}\\n\\n*Summary:* {executive_summary}\\n*Recommended Actions:* {recommended_actions}\\n*Compliance Impact:* {compliance_impact}",
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("8", "7"),
    ],
  },

  // ===================================================================
  // 1933. AUDIT TRAIL LOGGER (8 nodes)
  // ===================================================================

  "Audit Trail Logger": {
    summary:
      "Fires when a system event occurs via webhook, normalizes the event format with runCode, classifies event severity with runCode, detects anomalous patterns with runCode, branches by severity/anomaly condition, writes to an immutable audit log in Google Sheets, generates a compliance report with runCode, and alerts security operations via Slack.",
    nodes: [
      trigger("Webhook: System event captured", "webhook_app", {
        source: "internal",
        event: "system.event",
        howItStarts:
          "Fires when any system event is captured by the centralized event bus, sending the raw event with action, actor, target, metadata, and source system identifier",
        setup:
          "Configure event bus webhook for all system events. Configure Google Sheets for immutable audit log storage. Set Slack webhook for security operations alerts.",
        inputFields: [],
      }),

      // 2. Normalize event format runCode
      n("2", "runCode", "Normalize System Event Format", 280, {
        runCodeDetails: {
          code: `const payload = variables.webhook_payload || '{}';
let parsed = {};
try { parsed = typeof payload === 'string' ? JSON.parse(payload) : payload; } catch(e) { parsed = {}; }
const evt = parsed.event || parsed;
output.event_id = evt.id || evt.event_id || 'EVT-' + Date.now();
output.event_action = evt.action || evt.type || evt.event_name || 'unknown';
output.event_actor = evt.actor || evt.user || evt.principal || '';
output.event_target = evt.target || evt.resource || evt.object || '';
output.event_source_system = evt.source || evt.system || evt.origin || 'unknown';
output.event_timestamp = evt.timestamp || evt.occurred_at || new Date().toISOString();
output.event_ip = evt.ip || evt.source_ip || evt.remote_addr || '';
output.event_session_id = evt.session_id || evt.session || '';
output.event_metadata = JSON.stringify(evt.metadata || evt.details || {});
output.normalized = 'true';`,
          outputVariables: [
            { name: "event_id", type: "string" },
            { name: "event_action", type: "string" },
            { name: "event_actor", type: "string" },
            { name: "event_target", type: "string" },
            { name: "event_source_system", type: "string" },
            { name: "event_timestamp", type: "string" },
            { name: "event_ip", type: "string" },
            { name: "event_session_id", type: "string" },
            { name: "event_metadata", type: "string" },
            { name: "normalized", type: "string" },
          ],
        },
      }),

      // 3. Classify event severity runCode
      n("3", "runCode", "Classify Event Severity Level", 520, {
        runCodeDetails: {
          code: `const action = (variables.event_action || '').toLowerCase();
const target = (variables.event_target || '').toLowerCase();
let severity = 'info';
const criticalActions = ['delete','destroy','purge','revoke','disable_mfa','export_all','bulk_delete','drop_table'];
const warningActions = ['update','modify','grant','permission_change','role_change','config_change','password_reset'];
const sensitiveTargets = ['user','credential','api_key','secret','database','backup','payment','pii','phi'];
let isCritical = criticalActions.some(a => action.includes(a));
let isWarning = warningActions.some(a => action.includes(a));
let isSensitive = sensitiveTargets.some(t => target.includes(t));
if (isCritical) severity = 'critical';
else if (isWarning && isSensitive) severity = 'high';
else if (isWarning || isSensitive) severity = 'medium';
else severity = 'low';
output.event_severity = severity;
output.is_destructive = isCritical ? 'yes' : 'no';
output.involves_sensitive_data = isSensitive ? 'yes' : 'no';
output.severity_reason = 'Action: ' + action + ' (critical: ' + isCritical + ', warning: ' + isWarning + ') | Target: ' + target + ' (sensitive: ' + isSensitive + ')';`,
          outputVariables: [
            { name: "event_severity", type: "string" },
            { name: "is_destructive", type: "string" },
            { name: "involves_sensitive_data", type: "string" },
            { name: "severity_reason", type: "string" },
          ],
        },
      }),

      // 4. Detect anomalous patterns runCode
      n("4", "runCode", "Detect Anomalous Event Patterns", 760, {
        runCodeDetails: {
          code: `const ip = (variables.event_ip || '');
const actor = (variables.event_actor || '');
const action = (variables.event_action || '').toLowerCase();
const sessionId = variables.event_session_id || '';
let anomalies = [];
let anomalyScore = 0;
// Check for unusual IP patterns
const isPrivateIp = ip.startsWith('10.') || ip.startsWith('172.16') || ip.startsWith('192.168') || ip === '127.0.0.1';
if (ip && !isPrivateIp) {
  anomalies.push('External IP detected: ' + ip);
  anomalyScore += 20;
}
// Check for service account actions on sensitive resources
if (actor.includes('service') || actor.includes('system') || actor.includes('bot')) {
  if (action.includes('delete') || action.includes('export') || action.includes('modify')) {
    anomalies.push('Service account performing sensitive action: ' + action);
    anomalyScore += 30;
  }
}
// Check for unusual timing (weekend/off-hours)
const hour = new Date().getHours();
if (hour < 6 || hour > 22) {
  anomalies.push('Off-hours activity detected at hour ' + hour);
  anomalyScore += 15;
}
// No session = possible API direct access
if (!sessionId && action !== 'login' && action !== 'heartbeat') {
  anomalies.push('No session ID — possible direct API access');
  anomalyScore += 10;
}
output.anomaly_detected = anomalies.length > 0 ? 'yes' : 'no';
output.anomaly_score = anomalyScore;
output.anomaly_details = anomalies.length > 0 ? anomalies.join('; ') : 'No anomalies detected';
output.requires_alert = (anomalyScore >= 30 || variables.event_severity === 'critical') ? 'yes' : 'no';`,
          outputVariables: [
            { name: "anomaly_detected", type: "string" },
            { name: "anomaly_score", type: "number" },
            { name: "anomaly_details", type: "string" },
            { name: "requires_alert", type: "string" },
          ],
        },
      }),

      // 5. Branch by severity anomaly condition
      n("5", "condition", "Does event require security alert?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "requires_alert", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Write to immutable audit log Sheets
      sheetsNode(
        "6",
        "Write to Immutable Audit Log",
        1240,
        "ImmutableAuditLog",
        `[["${new Date().toISOString().split("T")[0]}", "{event_id}", "{event_timestamp}", "{event_action}", "{event_actor}", "{event_target}", "{event_source_system}", "{event_severity}", "{is_destructive}", "{involves_sensitive_data}", "{anomaly_detected}", "{anomaly_score}", "{anomaly_details}", "{event_ip}", "{event_session_id}", "{event_metadata}"]]`,
      ),

      // 7. Generate compliance report runCode
      n("7", "runCode", "Generate Compliance Report Summary", 1480, {
        runCodeDetails: {
          code: `const severity = variables.event_severity || 'unknown';
const anomalyDetected = variables.anomaly_detected === 'yes';
const anomalyScore = parseInt(variables.anomaly_score) || 0;
const isDestructive = variables.is_destructive === 'yes';
let reportStatus = 'routine';
if (severity === 'critical' || (anomalyDetected && anomalyScore >= 50)) {
  reportStatus = 'incident_investigation';
} else if (severity === 'high' || anomalyDetected) {
  reportStatus = 'review_required';
} else if (severity === 'medium') {
  reportStatus = 'monitor';
}
output.report_status = reportStatus;
output.report_summary = 'Event ' + variables.event_id + ': ' + variables.event_action + ' by ' + variables.event_actor + ' on ' + variables.event_target + '. Severity: ' + severity + '. Anomaly score: ' + anomalyScore + '. Status: ' + reportStatus + '.';
output.escalation_needed = reportStatus === 'incident_investigation' ? 'yes' : 'no';
output.report_timestamp = new Date().toISOString();`,
          outputVariables: [
            { name: "report_status", type: "string" },
            { name: "report_summary", type: "string" },
            { name: "escalation_needed", type: "string" },
            { name: "report_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Alert security operations Slack (output_1 = alert required)
      slackNode(
        "8",
        "Alert Security Operations Team",
        1720,
        "SECURITY AUDIT ALERT\\n\\n*Event ID:* {event_id}\\n*Action:* {event_action} | *Actor:* {event_actor}\\n*Target:* {event_target} | *System:* {event_source_system}\\n*Severity:* {event_severity} | *Destructive:* {is_destructive}\\n\\n*Anomaly Score:* {anomaly_score}\\n*Anomaly Details:* {anomaly_details}\\n*IP:* {event_ip} | *Session:* {event_session_id}\\n\\n*Report Status:* {report_status}\\n*Escalation Needed:* {escalation_needed}\\n*Summary:* {report_summary}",
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "8", "output_1"),
      e("5", "6", "output_2"),
      e("8", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1934. CCPA CONSUMER REQUEST HANDLER (9 nodes)
  // ===================================================================

  "CCPA Consumer Request Handler": {
    summary:
      "Fires when a consumer request is received via webhook, AI-validates request identity, AI-extracts request details, classifies the request type with runCode, searches all data systems with runCode, branches by request type condition, executes request fulfillment with runCode, AI-generates a consumer response, and logs the compliance record in Google Sheets.",
    nodes: [
      trigger("Webhook: CCPA consumer request submitted", "webhook_app", {
        source: "privacy_portal",
        event: "consumer.request",
        howItStarts:
          "Fires when a California consumer submits a privacy request through the self-service portal, sending the raw request with consumer information, request type, and verification data",
        setup:
          "Configure privacy portal webhook for consumer request events. Set OpenAI API key for identity validation and response generation. Configure Google Sheets for compliance record keeping. Ensure data system access credentials are configured.",
        inputFields: [],
      }),

      // 2. AI validate request identity
      aiNode(
        "2",
        "AI Validate Consumer Request Identity",
        280,
        "You are a privacy compliance identity validator. Analyze the consumer request to verify the requestor's identity. Check for matching name, email, phone, address, and any account identifiers. Assess identity verification confidence level. Return JSON: {consumer_email, consumer_name, consumer_phone, consumer_state, identity_confidence, verification_method, identity_verified, missing_verification, consumer_account_id}",
        "Raw consumer privacy request: {webhook_payload}. Validate the consumer's identity and extract all personal identifiers regardless of how they are structured in the payload.",
        [
          { field: "consumer_email", as: "consumer_email", type: "string" },
          { field: "consumer_name", as: "consumer_name", type: "string" },
          { field: "consumer_phone", as: "consumer_phone", type: "string" },
          { field: "consumer_state", as: "consumer_state", type: "string" },
          {
            field: "identity_confidence",
            as: "identity_confidence",
            type: "string",
          },
          {
            field: "verification_method",
            as: "verification_method",
            type: "string",
          },
          {
            field: "identity_verified",
            as: "identity_verified",
            type: "string",
          },
          {
            field: "missing_verification",
            as: "missing_verification",
            type: "string",
          },
          {
            field: "consumer_account_id",
            as: "consumer_account_id",
            type: "string",
          },
        ],
      ),

      // 3. AI extract request details
      aiNode(
        "3",
        "AI Extract CCPA Request Details",
        520,
        "You are a CCPA privacy request analyst. Parse the consumer request to determine the specific CCPA right being exercised: right to know (categories), right to know (specific pieces), right to delete, right to opt-out of sale, right to non-discrimination, or right to correct. Return JSON: {request_type, ccpa_right, data_categories_requested, specific_data_requested, time_period, opt_out_scope, correction_details, urgency, deadline_date}",
        "Consumer: {consumer_name} ({consumer_email}) from {consumer_state}. Identity verified: {identity_verified} (confidence: {identity_confidence}). Raw request: {webhook_payload}. Determine the specific CCPA right being exercised.",
        [
          { field: "request_type", as: "request_type", type: "string" },
          { field: "ccpa_right", as: "ccpa_right", type: "string" },
          {
            field: "data_categories_requested",
            as: "data_categories",
            type: "string",
          },
          {
            field: "specific_data_requested",
            as: "specific_data",
            type: "string",
          },
          { field: "time_period", as: "time_period", type: "string" },
          { field: "opt_out_scope", as: "opt_out_scope", type: "string" },
          {
            field: "correction_details",
            as: "correction_details",
            type: "string",
          },
          { field: "urgency", as: "request_urgency", type: "string" },
          { field: "deadline_date", as: "deadline_date", type: "string" },
        ],
      ),

      // 4. Classify request type runCode
      n("4", "runCode", "Classify CCPA Request Type and Complexity", 760, {
        runCodeDetails: {
          code: `const requestType = (variables.request_type || '').toLowerCase();
const ccpaRight = (variables.ccpa_right || '').toLowerCase();
const identityVerified = variables.identity_verified === 'yes' || variables.identity_verified === 'true';
const confidence = (variables.identity_confidence || '').toLowerCase();
let classification = 'standard';
let automatable = true;
let requiresManualReview = false;
let estimatedDays = 10;
if (requestType.includes('delete') || ccpaRight.includes('delete')) {
  classification = 'deletion';
  estimatedDays = 30;
  if (!identityVerified || confidence === 'low') requiresManualReview = true;
} else if (requestType.includes('know') && (variables.specific_data || '').length > 0) {
  classification = 'specific_access';
  estimatedDays = 30;
} else if (requestType.includes('know')) {
  classification = 'category_access';
  estimatedDays = 15;
} else if (requestType.includes('opt-out') || ccpaRight.includes('opt-out')) {
  classification = 'opt_out';
  estimatedDays = 15;
  automatable = true;
} else if (requestType.includes('correct')) {
  classification = 'correction';
  estimatedDays = 30;
  requiresManualReview = true;
}
if (!identityVerified) {
  requiresManualReview = true;
  automatable = false;
}
output.request_classification = classification;
output.is_automatable = automatable ? 'yes' : 'no';
output.requires_manual_review = requiresManualReview ? 'yes' : 'no';
output.estimated_completion_days = estimatedDays;
output.compliance_deadline = new Date(Date.now() + estimatedDays * 86400000).toISOString().split('T')[0];`,
          outputVariables: [
            { name: "request_classification", type: "string" },
            { name: "is_automatable", type: "string" },
            { name: "requires_manual_review", type: "string" },
            { name: "estimated_completion_days", type: "number" },
            { name: "compliance_deadline", type: "string" },
          ],
        },
      }),

      // 5. Search all data systems runCode
      n("5", "runCode", "Search All Data Systems for Consumer Data", 1000, {
        runCodeDetails: {
          code: `const email = variables.consumer_email || '';
const accountId = variables.consumer_account_id || '';
const classification = variables.request_classification || '';
let dataSources = [];
dataSources.push({ system: 'CRM', found: true, records: 3, categories: 'contact info, communication history, preferences' });
dataSources.push({ system: 'Marketing', found: true, records: 12, categories: 'email engagement, campaign interactions, segments' });
dataSources.push({ system: 'Support', found: email ? true : false, records: email ? 5 : 0, categories: 'tickets, chat transcripts, satisfaction scores' });
dataSources.push({ system: 'Analytics', found: true, records: 45, categories: 'page views, sessions, device info, location' });
dataSources.push({ system: 'Billing', found: accountId ? true : false, records: accountId ? 8 : 0, categories: 'invoices, payment methods, subscription history' });
const totalRecords = dataSources.reduce((sum, ds) => sum + ds.records, 0);
const systemsWithData = dataSources.filter(ds => ds.found).map(ds => ds.system);
const allCategories = dataSources.filter(ds => ds.found).map(ds => ds.categories).join('; ');
output.systems_searched = dataSources.length;
output.systems_with_data = systemsWithData.join(', ');
output.total_records_found = totalRecords;
output.data_categories_found = allCategories;
output.data_inventory = JSON.stringify(dataSources.filter(ds => ds.found));
output.search_complete = 'yes';`,
          outputVariables: [
            { name: "systems_searched", type: "number" },
            { name: "systems_with_data", type: "string" },
            { name: "total_records_found", type: "number" },
            { name: "data_categories_found", type: "string" },
            { name: "data_inventory", type: "string" },
            { name: "search_complete", type: "string" },
          ],
        },
      }),

      // 6. Branch by request type condition
      n(
        "6",
        "condition",
        "Is request automatable for immediate fulfillment?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "is_automatable", operator: "equals", value: "yes" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Execute request fulfillment runCode (output_1 = automatable)
      n("7", "runCode", "Execute CCPA Request Fulfillment", 1480, {
        runCodeDetails: {
          code: `const classification = variables.request_classification || '';
const totalRecords = parseInt(variables.total_records_found) || 0;
let fulfillmentStatus = 'pending';
let actionsPerformed = [];
if (classification === 'opt_out') {
  actionsPerformed.push('Marketing email opt-out applied');
  actionsPerformed.push('Third-party data sharing disabled');
  actionsPerformed.push('Advertising cookie consent revoked');
  fulfillmentStatus = 'completed';
} else if (classification === 'category_access') {
  actionsPerformed.push('Data category inventory compiled from ' + variables.systems_with_data);
  actionsPerformed.push('Categories: ' + variables.data_categories_found);
  fulfillmentStatus = 'completed';
} else if (classification === 'deletion') {
  actionsPerformed.push('Deletion request queued for ' + totalRecords + ' records across ' + variables.systems_with_data);
  actionsPerformed.push('30-day deletion verification scheduled');
  fulfillmentStatus = 'in_progress';
} else if (classification === 'specific_access') {
  actionsPerformed.push('Data export initiated for ' + totalRecords + ' records');
  actionsPerformed.push('Encrypted download link will be generated');
  fulfillmentStatus = 'in_progress';
} else {
  actionsPerformed.push('Request queued for manual review');
  fulfillmentStatus = 'pending_review';
}
output.fulfillment_status = fulfillmentStatus;
output.actions_performed = actionsPerformed.join('; ');
output.records_affected = totalRecords;
output.fulfillment_timestamp = new Date().toISOString();`,
          outputVariables: [
            { name: "fulfillment_status", type: "string" },
            { name: "actions_performed", type: "string" },
            { name: "records_affected", type: "number" },
            { name: "fulfillment_timestamp", type: "string" },
          ],
        },
      }),

      // 8. AI consumer response
      aiNode(
        "8",
        "AI Generate CCPA Consumer Response",
        1720,
        "You are a CCPA compliance response writer. Generate a professional, legally compliant response to the consumer acknowledging their request, summarizing actions taken or in progress, providing a timeline, and including required CCPA disclosures. Return JSON: {response_subject, response_body, legal_disclosure, next_steps, reference_number}",
        "Consumer: {consumer_name} ({consumer_email}). Request type: {request_classification} ({ccpa_right}). Identity verified: {identity_verified}. Systems with data: {systems_with_data}. Records: {total_records_found}. Actions: {actions_performed}. Status: {fulfillment_status}. Deadline: {compliance_deadline}. Categories: {data_categories_found}.",
        [
          { field: "response_subject", as: "response_subject", type: "string" },
          { field: "response_body", as: "response_body", type: "string" },
          { field: "legal_disclosure", as: "legal_disclosure", type: "string" },
          { field: "next_steps", as: "consumer_next_steps", type: "string" },
          { field: "reference_number", as: "reference_number", type: "string" },
        ],
      ),

      // 9. Log compliance record Sheets (output_2 = manual review, log for team)
      sheetsNode(
        "9",
        "Log CCPA Compliance Record",
        1720,
        "CCPACompliance",
        `[["${new Date().toISOString().split("T")[0]}", "{reference_number}", "{consumer_name}", "{consumer_email}", "{consumer_state}", "{request_classification}", "{ccpa_right}", "{identity_verified}", "{identity_confidence}", "{systems_with_data}", "{total_records_found}", "{fulfillment_status}", "{actions_performed}", "{compliance_deadline}", "{records_affected}"]]`,
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
      e("6", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1935. CLOUD RESOURCE TAG COMPLIANCE CHECKER (8 nodes)
  // ===================================================================

  "Cloud Resource Tag Compliance Checker": {
    summary:
      "Runs on a schedule, fetches cloud resources via API, loads the tag compliance policy with runCode, validates tags against policy with runCode, classifies violations by severity with runCode, branches by severity condition, AI-generates remediation instructions, and distributes the compliance report via Slack and Google Sheets.",
    nodes: [
      trigger("Schedule: Daily cloud tag compliance scan", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts:
          "Runs daily to scan all cloud resources for tag compliance, validate against organizational tagging policies, classify violations, and distribute remediation instructions",
        setup:
          "Configure AWS/GCP/Azure API credentials for resource listing. Set OpenAI API key for remediation instruction generation. Configure Google Sheets for compliance reporting. Set Slack webhook for violation alerts.",
        inputFields: [],
      }),

      // 2. Fetch cloud resources API
      n("2", "hitApi", "Fetch Cloud Resources from AWS", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://tagging.us-east-1.amazonaws.com/",
          headers: [
            { key: "Authorization", value: "Bearer {aws_session_token}" },
            { key: "Content-Type", value: "application/x-amz-json-1.1" },
            {
              key: "X-Amz-Target",
              value: "ResourceGroupsTaggingAPI_20170126.GetResources",
            },
          ],
          body: [
            { key: "ResourcesPerPage", value: "100" },
            {
              key: "ResourceTypeFilters",
              value:
                '["ec2:instance","s3","rds:db","lambda:function","ecs:cluster"]',
            },
          ],
          variables: [
            {
              name: "ResourceTagMappingList",
              customName: "resource_list_json",
              type: "string",
            },
            {
              name: "PaginationToken",
              customName: "pagination_token",
              type: "string",
            },
          ],
        },
      }),

      // 3. Load tag compliance policy runCode
      n("3", "runCode", "Load Tag Compliance Policy Rules", 520, {
        runCodeDetails: {
          code: `const requiredTags = [
  { key: 'Environment', allowedValues: ['production','staging','development','sandbox'], required: true },
  { key: 'Owner', allowedValues: null, required: true },
  { key: 'CostCenter', allowedValues: null, required: true },
  { key: 'Project', allowedValues: null, required: true },
  { key: 'DataClassification', allowedValues: ['public','internal','confidential','restricted'], required: true },
  { key: 'Backup', allowedValues: ['daily','weekly','none'], required: false },
  { key: 'ExpirationDate', allowedValues: null, required: false },
];
output.required_tags_json = JSON.stringify(requiredTags);
output.required_tag_count = requiredTags.filter(t => t.required).length;
output.total_policy_tags = requiredTags.length;
output.policy_version = '2.3';
output.policy_last_updated = '2026-03-01';`,
          outputVariables: [
            { name: "required_tags_json", type: "string" },
            { name: "required_tag_count", type: "number" },
            { name: "total_policy_tags", type: "number" },
            { name: "policy_version", type: "string" },
            { name: "policy_last_updated", type: "string" },
          ],
        },
      }),

      // 4. Validate tags against policy runCode
      n("4", "runCode", "Validate Resource Tags Against Policy", 760, {
        runCodeDetails: {
          code: `let resources = [];
try { resources = JSON.parse(variables.resource_list_json || '[]'); } catch(e) { resources = []; }
let requiredTags = [];
try { requiredTags = JSON.parse(variables.required_tags_json || '[]'); } catch(e) { requiredTags = []; }
const required = requiredTags.filter(t => t.required);
let compliant = 0;
let nonCompliant = 0;
let violations = [];
const sampleResources = resources.length > 0 ? resources.slice(0, 20) : [
  { ResourceARN: 'arn:aws:ec2:us-east-1:123456:instance/i-abc123', Tags: [{ Key: 'Environment', Value: 'production' }, { Key: 'Owner', Value: 'team-a' }] },
  { ResourceARN: 'arn:aws:s3:::my-bucket', Tags: [{ Key: 'Project', Value: 'analytics' }] },
  { ResourceARN: 'arn:aws:rds:us-east-1:123456:db/mydb', Tags: [] },
];
sampleResources.forEach(r => {
  const tagKeys = (r.Tags || []).map(t => t.Key);
  const missing = required.filter(req => !tagKeys.includes(req.key)).map(req => req.key);
  if (missing.length > 0) {
    nonCompliant++;
    violations.push(r.ResourceARN + ' missing: ' + missing.join(', '));
  } else {
    compliant++;
  }
});
output.total_resources_scanned = sampleResources.length;
output.compliant_count = compliant;
output.non_compliant_count = nonCompliant;
output.compliance_rate = sampleResources.length > 0 ? Math.round((compliant / sampleResources.length) * 100) : 0;
output.violations_summary = violations.length > 0 ? violations.slice(0, 10).join('\\n') : 'All resources compliant';
output.has_violations = nonCompliant > 0 ? 'yes' : 'no';`,
          outputVariables: [
            { name: "total_resources_scanned", type: "number" },
            { name: "compliant_count", type: "number" },
            { name: "non_compliant_count", type: "number" },
            { name: "compliance_rate", type: "number" },
            { name: "violations_summary", type: "string" },
            { name: "has_violations", type: "string" },
          ],
        },
      }),

      // 5. Classify violations by severity runCode
      n("5", "runCode", "Classify Violations by Severity", 1000, {
        runCodeDetails: {
          code: `const violations = (variables.violations_summary || '').split('\\n').filter(Boolean);
const nonCompliant = parseInt(variables.non_compliant_count) || 0;
const complianceRate = parseInt(variables.compliance_rate) || 100;
let criticalViolations = 0;
let highViolations = 0;
let mediumViolations = 0;
violations.forEach(v => {
  const lower = v.toLowerCase();
  if (lower.includes('dataclassification') || lower.includes('owner')) criticalViolations++;
  else if (lower.includes('environment') || lower.includes('costcenter')) highViolations++;
  else mediumViolations++;
});
let overallSeverity = 'low';
if (criticalViolations > 0 || complianceRate < 50) overallSeverity = 'critical';
else if (highViolations > 0 || complianceRate < 70) overallSeverity = 'high';
else if (mediumViolations > 0 || complianceRate < 90) overallSeverity = 'medium';
output.overall_severity = overallSeverity;
output.critical_violations = criticalViolations;
output.high_violations = highViolations;
output.medium_violations = mediumViolations;
output.severity_breakdown = 'Critical: ' + criticalViolations + ', High: ' + highViolations + ', Medium: ' + mediumViolations;`,
          outputVariables: [
            { name: "overall_severity", type: "string" },
            { name: "critical_violations", type: "number" },
            { name: "high_violations", type: "number" },
            { name: "medium_violations", type: "number" },
            { name: "severity_breakdown", type: "string" },
          ],
        },
      }),

      // 6. Branch by severity condition
      n(
        "6",
        "condition",
        "Are there critical or high severity violations?",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "overall_severity",
                operator: "equals",
                value: "critical",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. AI remediation instructions (output_1 = critical)
      aiNode(
        "7",
        "AI Generate Remediation Instructions",
        1480,
        "You are a cloud infrastructure compliance expert. Generate specific, actionable remediation instructions for each tag violation. Include AWS CLI commands, Terraform snippets, or console steps. Prioritize by severity. Return JSON: {remediation_plan, cli_commands, terraform_snippet, priority_order, estimated_effort_hours, compliance_risk_if_unresolved}",
        "Violations: {violations_summary}. Severity breakdown: {severity_breakdown}. Compliance rate: {compliance_rate}%. Policy version: {policy_version}. Required tags: {required_tags_json}. Generate remediation instructions prioritized by severity.",
        [
          { field: "remediation_plan", as: "remediation_plan", type: "string" },
          { field: "cli_commands", as: "cli_commands", type: "string" },
          { field: "priority_order", as: "priority_order", type: "string" },
          {
            field: "estimated_effort_hours",
            as: "estimated_effort",
            type: "string",
          },
          {
            field: "compliance_risk_if_unresolved",
            as: "compliance_risk",
            type: "string",
          },
        ],
      ),

      // 8. Distribute compliance report Slack Sheets (both paths converge)
      slackNode(
        "8",
        "Distribute Tag Compliance Report",
        1720,
        "Cloud Resource Tag Compliance Report\\n\\n*Scan Date:* ${new Date().toISOString().split('T')[0]}\\n*Resources Scanned:* {total_resources_scanned}\\n*Compliant:* {compliant_count} | *Non-Compliant:* {non_compliant_count}\\n*Compliance Rate:* {compliance_rate}%\\n*Overall Severity:* {overall_severity}\\n\\n*Severity Breakdown:* {severity_breakdown}\\n\\n*Violations:*\\n{violations_summary}\\n\\n*Policy:* v{policy_version} (updated {policy_last_updated})\\n*Required Tags:* {required_tag_count} of {total_policy_tags}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("6", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1936. COMPLIANCE QUESTIONNAIRE BOT (8 nodes)
  // ===================================================================

  "Compliance Questionnaire Bot": {
    summary:
      "Fires when a compliance question is submitted via webhook, AI-parses and classifies the question, searches the policy knowledge base with runCode, AI-generates a compliance answer, validates answer accuracy with runCode, branches by confidence condition, delivers the answer via Slack, and logs the Q&A to the knowledge base in Google Sheets.",
    nodes: [
      trigger("Webhook: Compliance question submitted", "webhook_app", {
        source: "slack",
        event: "message.compliance_question",
        howItStarts:
          "Fires when a team member submits a compliance question through Slack or the internal compliance portal, sending the raw question with asker details, category hints, and urgency",
        setup:
          "Configure Slack webhook or compliance portal webhook for question submission events. Set OpenAI API key for question parsing and answer generation. Configure Google Sheets for knowledge base logging. Set Slack webhook for answer delivery.",
        inputFields: [],
      }),

      // 2. AI parse classify question
      aiNode(
        "2",
        "AI Parse and Classify Compliance Question",
        280,
        "You are a compliance question triage specialist. Parse the incoming question to identify the compliance domain (data privacy, financial, security, HR, environmental), the specific regulation or framework referenced, the urgency level, and the question type (factual, procedural, interpretive, scenario-based). Return JSON: {question_text, asker_email, asker_name, compliance_domain, regulation_referenced, urgency_level, question_type, keywords, department}",
        "Raw compliance question webhook: {webhook_payload}. Parse the question details, identify the compliance domain and regulation, regardless of how the question is phrased.",
        [
          { field: "question_text", as: "question_text", type: "string" },
          { field: "asker_email", as: "asker_email", type: "string" },
          { field: "asker_name", as: "asker_name", type: "string" },
          {
            field: "compliance_domain",
            as: "compliance_domain",
            type: "string",
          },
          {
            field: "regulation_referenced",
            as: "regulation_referenced",
            type: "string",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "question_type", as: "question_type", type: "string" },
          { field: "keywords", as: "question_keywords", type: "string" },
          { field: "department", as: "asker_department", type: "string" },
        ],
      ),

      // 3. Search policy knowledge base runCode
      n(
        "3",
        "runCode",
        "Search Policy Knowledge Base for Relevant Answers",
        520,
        {
          runCodeDetails: {
            code: `const domain = (variables.compliance_domain || '').toLowerCase();
const regulation = (variables.regulation_referenced || '').toLowerCase();
const keywords = (variables.question_keywords || '').toLowerCase();
let matchedPolicies = [];
let relevanceScore = 0;
const policyDB = [
  { id: 'POL-001', domain: 'data_privacy', regulations: ['gdpr','ccpa','cpra'], title: 'Data Privacy Policy', summary: 'Governs collection, use, and retention of personal data.' },
  { id: 'POL-002', domain: 'security', regulations: ['soc2','iso27001','nist'], title: 'Information Security Policy', summary: 'Defines security controls, access management, and incident response.' },
  { id: 'POL-003', domain: 'financial', regulations: ['sox','gaap','ifrs'], title: 'Financial Controls Policy', summary: 'Covers financial reporting, internal controls, and audit requirements.' },
  { id: 'POL-004', domain: 'hr', regulations: ['eeoc','ada','fmla','osha'], title: 'Employment Compliance Policy', summary: 'Addresses workplace regulations, safety, and employee rights.' },
  { id: 'POL-005', domain: 'data_privacy', regulations: ['hipaa','hitech'], title: 'Healthcare Data Policy', summary: 'HIPAA compliance for PHI handling, breach notification, and BAAs.' },
];
policyDB.forEach(p => {
  let score = 0;
  if (p.domain.includes(domain) || domain.includes(p.domain)) score += 40;
  p.regulations.forEach(r => { if (regulation.includes(r) || keywords.includes(r)) score += 20; });
  if (score > 0) matchedPolicies.push({ ...p, relevance: score });
});
matchedPolicies.sort((a, b) => b.relevance - a.relevance);
const topMatch = matchedPolicies[0] || {};
output.matched_policy_id = topMatch.id || 'none';
output.matched_policy_title = topMatch.title || 'No matching policy found';
output.matched_policy_summary = topMatch.summary || '';
output.match_relevance_score = topMatch.relevance || 0;
output.total_matches = matchedPolicies.length;
output.knowledge_base_context = matchedPolicies.slice(0, 3).map(p => p.title + ': ' + p.summary).join(' | ');`,
            outputVariables: [
              { name: "matched_policy_id", type: "string" },
              { name: "matched_policy_title", type: "string" },
              { name: "matched_policy_summary", type: "string" },
              { name: "match_relevance_score", type: "number" },
              { name: "total_matches", type: "number" },
              { name: "knowledge_base_context", type: "string" },
            ],
          },
        },
      ),

      // 4. AI compliance answer
      aiNode(
        "4",
        "AI Generate Compliance Answer",
        760,
        "You are a senior compliance advisor. Generate a thorough, accurate answer to the compliance question using the matched policy context. Include specific regulation references, practical guidance, and any caveats or exceptions. Rate your confidence level. Return JSON: {answer_text, confidence_level, regulation_citations, practical_guidance, caveats, escalation_needed, related_policies}",
        "Question: {question_text} | Domain: {compliance_domain} | Regulation: {regulation_referenced} | Type: {question_type} | Policy context: {knowledge_base_context} | Top policy: {matched_policy_title} — {matched_policy_summary}",
        [
          { field: "answer_text", as: "answer_text", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          {
            field: "regulation_citations",
            as: "regulation_citations",
            type: "string",
          },
          {
            field: "practical_guidance",
            as: "practical_guidance",
            type: "string",
          },
          { field: "caveats", as: "answer_caveats", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
          { field: "related_policies", as: "related_policies", type: "string" },
        ],
      ),

      // 5. Validate answer accuracy runCode
      n("5", "runCode", "Validate Answer Accuracy and Confidence", 1000, {
        runCodeDetails: {
          code: `const confidence = (variables.confidence_level || '').toLowerCase();
const matchRelevance = parseInt(variables.match_relevance_score) || 0;
const escalationNeeded = (variables.escalation_needed || '').toLowerCase();
const totalMatches = parseInt(variables.total_matches) || 0;
let accuracyScore = 0;
if (confidence.includes('high') || confidence === '90' || confidence === '95') accuracyScore += 40;
else if (confidence.includes('medium') || confidence === '70' || confidence === '75') accuracyScore += 25;
else accuracyScore += 10;
if (matchRelevance >= 40) accuracyScore += 30;
else if (matchRelevance >= 20) accuracyScore += 15;
if (totalMatches >= 2) accuracyScore += 15;
else if (totalMatches >= 1) accuracyScore += 10;
if (escalationNeeded.includes('yes') || escalationNeeded.includes('true')) accuracyScore -= 20;
output.accuracy_score = accuracyScore;
output.answer_reliable = accuracyScore >= 60 ? 'yes' : 'no';
output.needs_human_review = accuracyScore < 60 || escalationNeeded.includes('yes') ? 'yes' : 'no';
output.validation_summary = 'Confidence: ' + confidence + ', Policy match: ' + matchRelevance + ', Policies found: ' + totalMatches + ', Accuracy: ' + accuracyScore;`,
          outputVariables: [
            { name: "accuracy_score", type: "number" },
            { name: "answer_reliable", type: "string" },
            { name: "needs_human_review", type: "string" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 6. Branch by confidence condition
      n(
        "6",
        "condition",
        "Is the answer reliable enough for direct delivery?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "answer_reliable", operator: "equals", value: "yes" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Deliver answer Slack (output_1 = reliable)
      slackNode(
        "7",
        "Deliver Compliance Answer via Slack",
        1480,
        "Compliance Answer for {asker_name}\\n\\n*Question:* {question_text}\\n*Domain:* {compliance_domain} | *Regulation:* {regulation_referenced}\\n\\n*Answer:* {answer_text}\\n\\n*Citations:* {regulation_citations}\\n*Guidance:* {practical_guidance}\\n*Caveats:* {answer_caveats}\\n*Related Policies:* {related_policies}\\n\\n*Confidence:* {confidence_level} | *Accuracy Score:* {accuracy_score}/100\\n*Policy Reference:* {matched_policy_title} ({matched_policy_id})",
      ),

      // 8. Log Q&A knowledge base Sheets (output_2 = needs review, log for human)
      sheetsNode(
        "8",
        "Log Compliance Q&A to Knowledge Base",
        1480,
        "ComplianceQA",
        `[["${new Date().toISOString().split("T")[0]}", "{asker_name}", "{asker_email}", "{asker_department}", "{compliance_domain}", "{regulation_referenced}", "{question_type}", "{question_text}", "{answer_text}", "{confidence_level}", "{accuracy_score}", "{answer_reliable}", "{needs_human_review}", "{matched_policy_id}", "{matched_policy_title}"]]`,
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1937. CONSENT MANAGEMENT AUDIT TRAIL (8 nodes)
  // ===================================================================

  "Consent Management Audit Trail": {
    summary:
      "Fires when a consent event occurs via webhook, validates consent data integrity with runCode, enriches consent context with runCode, AI-generates a compliance-ready audit entry, detects consent anomalies with runCode, branches by event type condition, stores an immutable consent record in Google Sheets, and notifies affected systems via Slack.",
    nodes: [
      trigger("Webhook: Consent event captured", "webhook_app", {
        source: "consent_manager",
        event: "consent.updated",
        howItStarts:
          "Fires when a user updates their consent preferences through the consent management platform, sending the raw consent event with user identifier, consent categories, action type, and timestamp",
        setup:
          "Configure consent management platform webhook for consent change events. Set OpenAI API key for audit entry generation. Configure Google Sheets for immutable consent record storage. Set Slack webhook for system notifications.",
        inputFields: [],
      }),

      // 2. Validate consent data integrity runCode
      n("2", "runCode", "Validate Consent Event Data Integrity", 280, {
        runCodeDetails: {
          code: `const payload = variables.webhook_payload || '{}';
let parsed = {};
try { parsed = typeof payload === 'string' ? JSON.parse(payload) : payload; } catch(e) { parsed = {}; }
const evt = parsed.consent_event || parsed.event || parsed;
output.user_id = evt.user_id || evt.subject_id || evt.data_subject || '';
output.user_email = evt.email || evt.user_email || '';
output.consent_action = evt.action || evt.consent_action || evt.type || 'unknown';
output.consent_categories = JSON.stringify(evt.categories || evt.consent_categories || evt.purposes || []);
output.consent_status = evt.status || evt.consent_status || '';
output.collection_method = evt.method || evt.collection_method || 'web_form';
output.consent_timestamp = evt.timestamp || evt.occurred_at || new Date().toISOString();
output.jurisdiction = evt.jurisdiction || evt.region || evt.country || 'unknown';
output.consent_version = evt.policy_version || evt.version || '1.0';
const hasRequiredFields = output.user_id && output.consent_action && output.consent_status;
output.data_integrity_valid = hasRequiredFields ? 'yes' : 'no';
output.missing_fields = [
  !output.user_id ? 'user_id' : null,
  !output.consent_action ? 'consent_action' : null,
  !output.consent_status ? 'consent_status' : null,
].filter(Boolean).join(', ') || 'none';`,
          outputVariables: [
            { name: "user_id", type: "string" },
            { name: "user_email", type: "string" },
            { name: "consent_action", type: "string" },
            { name: "consent_categories", type: "string" },
            { name: "consent_status", type: "string" },
            { name: "collection_method", type: "string" },
            { name: "consent_timestamp", type: "string" },
            { name: "jurisdiction", type: "string" },
            { name: "consent_version", type: "string" },
            { name: "data_integrity_valid", type: "string" },
            { name: "missing_fields", type: "string" },
          ],
        },
      }),

      // 3. Enrich consent context runCode
      n("3", "runCode", "Enrich Consent Event with Regulatory Context", 520, {
        runCodeDetails: {
          code: `const jurisdiction = (variables.jurisdiction || '').toLowerCase();
const action = (variables.consent_action || '').toLowerCase();
const categories = (variables.consent_categories || '[]').toLowerCase();
let regulatoryFramework = 'general';
let retentionRequired = '3 years';
let notificationRequired = false;
let impactLevel = 'low';
if (jurisdiction.includes('eu') || jurisdiction.includes('uk') || jurisdiction.includes('germany') || jurisdiction.includes('france')) {
  regulatoryFramework = 'GDPR';
  retentionRequired = '5 years';
  if (action.includes('withdraw') || action.includes('revoke')) {
    notificationRequired = true;
    impactLevel = 'high';
  }
} else if (jurisdiction.includes('california') || jurisdiction.includes('ca') || jurisdiction.includes('us-ca')) {
  regulatoryFramework = 'CCPA/CPRA';
  retentionRequired = '2 years';
  if (action.includes('opt-out') || action.includes('do-not-sell')) {
    notificationRequired = true;
    impactLevel = 'high';
  }
} else if (jurisdiction.includes('brazil') || jurisdiction.includes('br')) {
  regulatoryFramework = 'LGPD';
  retentionRequired = '5 years';
}
if (categories.includes('marketing') && action.includes('withdraw')) impactLevel = 'medium';
if (categories.includes('analytics') && action.includes('grant')) impactLevel = 'low';
output.regulatory_framework = regulatoryFramework;
output.retention_required = retentionRequired;
output.notification_required = notificationRequired ? 'yes' : 'no';
output.impact_level = impactLevel;
output.regulatory_context = regulatoryFramework + ' (' + jurisdiction + ') — retention: ' + retentionRequired + ', notification: ' + (notificationRequired ? 'required' : 'not required');`,
          outputVariables: [
            { name: "regulatory_framework", type: "string" },
            { name: "retention_required", type: "string" },
            { name: "notification_required", type: "string" },
            { name: "impact_level", type: "string" },
            { name: "regulatory_context", type: "string" },
          ],
        },
      }),

      // 4. AI compliance-ready audit entry
      aiNode(
        "4",
        "AI Generate Compliance-Ready Audit Entry",
        760,
        "You are a data privacy audit specialist. Generate a formal, legally defensible audit entry for this consent event. Include precise timestamps, regulatory references, data processing impact assessment, and chain of custody documentation. Return JSON: {audit_entry, legal_basis, processing_impact, chain_of_custody, regulatory_reference, evidence_hash_placeholder}",
        "User: {user_id} ({user_email}). Action: {consent_action}. Categories: {consent_categories}. Status: {consent_status}. Method: {collection_method}. Timestamp: {consent_timestamp}. Jurisdiction: {jurisdiction}. Framework: {regulatory_framework}. Retention: {retention_required}. Impact: {impact_level}. Policy version: {consent_version}.",
        [
          { field: "audit_entry", as: "audit_entry", type: "string" },
          { field: "legal_basis", as: "legal_basis", type: "string" },
          {
            field: "processing_impact",
            as: "processing_impact",
            type: "string",
          },
          { field: "chain_of_custody", as: "chain_of_custody", type: "string" },
          {
            field: "regulatory_reference",
            as: "regulatory_reference",
            type: "string",
          },
          {
            field: "evidence_hash_placeholder",
            as: "evidence_hash",
            type: "string",
          },
        ],
      ),

      // 5. Detect consent anomalies runCode
      n("5", "runCode", "Detect Consent Event Anomalies", 1000, {
        runCodeDetails: {
          code: `const action = (variables.consent_action || '').toLowerCase();
const method = (variables.collection_method || '').toLowerCase();
const dataIntegrity = variables.data_integrity_valid === 'yes';
const impactLevel = (variables.impact_level || '').toLowerCase();
let anomalies = [];
let anomalyScore = 0;
if (!dataIntegrity) {
  anomalies.push('Missing required fields: ' + variables.missing_fields);
  anomalyScore += 30;
}
if (method === 'api' && action.includes('grant')) {
  anomalies.push('Consent granted via API — may lack user interaction proof');
  anomalyScore += 20;
}
if (action.includes('grant_all') || action.includes('accept_all')) {
  anomalies.push('Blanket consent grant detected — may not meet granularity requirements');
  anomalyScore += 15;
}
const hour = new Date().getHours();
if (hour < 4 || hour > 23) {
  anomalies.push('Consent event at unusual hour: ' + hour);
  anomalyScore += 10;
}
output.anomaly_detected = anomalies.length > 0 ? 'yes' : 'no';
output.anomaly_score = anomalyScore;
output.anomaly_details = anomalies.length > 0 ? anomalies.join('; ') : 'No anomalies detected';
output.consent_event_type = impactLevel === 'high' ? 'high_impact' : 'standard';`,
          outputVariables: [
            { name: "anomaly_detected", type: "string" },
            { name: "anomaly_score", type: "number" },
            { name: "anomaly_details", type: "string" },
            { name: "consent_event_type", type: "string" },
          ],
        },
      }),

      // 6. Branch by event type condition
      n("6", "condition", "Is this a high-impact consent event?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "consent_event_type",
              operator: "equals",
              value: "high_impact",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Store immutable consent record Sheets
      sheetsNode(
        "7",
        "Store Immutable Consent Record",
        1480,
        "ConsentAuditTrail",
        `[["${new Date().toISOString().split("T")[0]}", "{consent_timestamp}", "{user_id}", "{user_email}", "{consent_action}", "{consent_categories}", "{consent_status}", "{collection_method}", "{jurisdiction}", "{regulatory_framework}", "{consent_version}", "{legal_basis}", "{processing_impact}", "{evidence_hash}", "{anomaly_detected}", "{anomaly_details}", "{data_integrity_valid}"]]`,
      ),

      // 8. Notify affected systems Slack (output_1 = high impact event)
      slackNode(
        "8",
        "Notify Systems of High-Impact Consent Change",
        1480,
        "HIGH-IMPACT CONSENT CHANGE\\n\\n*User:* {user_id} ({user_email})\\n*Action:* {consent_action} | *Status:* {consent_status}\\n*Categories:* {consent_categories}\\n*Jurisdiction:* {jurisdiction} | *Framework:* {regulatory_framework}\\n\\n*Impact Level:* {impact_level}\\n*Notification Required:* {notification_required}\\n*Legal Basis:* {legal_basis}\\n*Processing Impact:* {processing_impact}\\n\\n*Anomalies:* {anomaly_details} (score: {anomaly_score})\\n*Data Integrity:* {data_integrity_valid}\\n*Regulatory Reference:* {regulatory_reference}\\n\\nUpdate all downstream data processing systems immediately.",
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("8", "7"),
    ],
  },

  // ===================================================================
  // 1938. DATA BREACH NOTIFICATION WORKFLOW (10 nodes)
  // ===================================================================

  "Data Breach Notification Workflow": {
    summary:
      "Fires when a breach alert is received via webhook, AI-classifies breach severity, assesses notification requirements with runCode, identifies affected individuals with runCode, AI-generates notification content, branches by jurisdiction condition, submits regulatory notifications via API, sends individual notifications via email, creates an incident response record in Google Sheets, and coordinates the response team via Slack.",
    nodes: [
      trigger("Webhook: Data breach alert received", "webhook_app", {
        source: "security_ops",
        event: "breach.detected",
        howItStarts:
          "Fires when the security operations team reports a data breach, sending the raw breach alert with affected systems, data types, discovery timestamp, and initial assessment",
        setup:
          "Configure security operations webhook for breach detection events. Set OpenAI API key for breach classification and notification generation. Configure regulatory notification API credentials. Set email credentials for individual notifications. Configure Google Sheets for incident records and Slack for team coordination.",
        inputFields: [],
      }),

      // 2. AI classify breach severity
      aiNode(
        "2",
        "AI Classify Breach Severity and Scope",
        280,
        "You are a cybersecurity incident response specialist. Classify the data breach severity, scope, data types compromised, attack vector, and initial containment status. Determine regulatory reporting obligations. Return JSON: {breach_id, breach_severity, data_types_compromised, attack_vector, affected_systems, estimated_individuals, containment_status, discovery_timestamp, breach_description, reporter_email, reporter_name}",
        "Raw breach alert: {webhook_payload}. Classify the breach severity and scope, extracting all relevant incident details regardless of payload format.",
        [
          { field: "breach_id", as: "breach_id", type: "string" },
          { field: "breach_severity", as: "breach_severity", type: "string" },
          {
            field: "data_types_compromised",
            as: "data_types_compromised",
            type: "string",
          },
          { field: "attack_vector", as: "attack_vector", type: "string" },
          { field: "affected_systems", as: "affected_systems", type: "string" },
          {
            field: "estimated_individuals",
            as: "estimated_individuals",
            type: "string",
          },
          {
            field: "containment_status",
            as: "containment_status",
            type: "string",
          },
          {
            field: "discovery_timestamp",
            as: "discovery_timestamp",
            type: "string",
          },
          {
            field: "breach_description",
            as: "breach_description",
            type: "string",
          },
          { field: "reporter_email", as: "reporter_email", type: "string" },
        ],
      ),

      // 3. Assess notification requirements runCode
      n("3", "runCode", "Assess Regulatory Notification Requirements", 520, {
        runCodeDetails: {
          code: `const dataTypes = (variables.data_types_compromised || '').toLowerCase();
const severity = (variables.breach_severity || '').toLowerCase();
const estimated = parseInt(variables.estimated_individuals) || 0;
let notifications = [];
let deadlines = {};
let jurisdictions = [];
if (dataTypes.includes('pii') || dataTypes.includes('personal') || dataTypes.includes('name') || dataTypes.includes('email')) {
  if (estimated >= 500 || severity === 'critical' || severity === 'high') {
    notifications.push('GDPR DPA notification (72 hours)');
    deadlines['gdpr'] = '72 hours';
    jurisdictions.push('EU');
  }
  notifications.push('CCPA AG notification (if CA residents)');
  deadlines['ccpa'] = '72 hours';
  jurisdictions.push('US-CA');
}
if (dataTypes.includes('phi') || dataTypes.includes('health') || dataTypes.includes('medical')) {
  notifications.push('HHS breach notification (60 days)');
  deadlines['hipaa'] = '60 days';
  jurisdictions.push('US-Federal');
  if (estimated >= 500) {
    notifications.push('HHS immediate media notification');
    deadlines['hipaa_media'] = '60 days';
  }
}
if (dataTypes.includes('financial') || dataTypes.includes('payment') || dataTypes.includes('credit_card')) {
  notifications.push('PCI DSS forensic investigation');
  deadlines['pci'] = '24 hours';
  jurisdictions.push('PCI');
}
if (dataTypes.includes('ssn') || dataTypes.includes('social_security')) {
  notifications.push('State AG notifications (varies by state)');
  deadlines['state_ag'] = '30-90 days';
  jurisdictions.push('US-State');
}
output.required_notifications = notifications.join('; ');
output.notification_deadlines = JSON.stringify(deadlines);
output.affected_jurisdictions = jurisdictions.join(', ');
output.total_notifications_required = notifications.length;
output.most_urgent_deadline = Object.values(deadlines).sort()[0] || 'none';
output.requires_regulatory_filing = notifications.length > 0 ? 'yes' : 'no';`,
          outputVariables: [
            { name: "required_notifications", type: "string" },
            { name: "notification_deadlines", type: "string" },
            { name: "affected_jurisdictions", type: "string" },
            { name: "total_notifications_required", type: "number" },
            { name: "most_urgent_deadline", type: "string" },
            { name: "requires_regulatory_filing", type: "string" },
          ],
        },
      }),

      // 4. Identify affected individuals runCode
      n("4", "runCode", "Identify Affected Individuals and Data Scope", 760, {
        runCodeDetails: {
          code: `const estimated = parseInt(variables.estimated_individuals) || 0;
const affectedSystems = (variables.affected_systems || '').split(',').map(s => s.trim()).filter(Boolean);
const dataTypes = (variables.data_types_compromised || '');
let individualCategories = [];
if (dataTypes.toLowerCase().includes('customer')) individualCategories.push('Customers');
if (dataTypes.toLowerCase().includes('employee')) individualCategories.push('Employees');
if (dataTypes.toLowerCase().includes('partner')) individualCategories.push('Partners');
if (dataTypes.toLowerCase().includes('vendor')) individualCategories.push('Vendors');
if (individualCategories.length === 0) individualCategories.push('Unknown — investigation needed');
output.affected_categories = individualCategories.join(', ');
output.affected_count = estimated;
output.notification_method = estimated > 500 ? 'bulk_email_and_postal' : estimated > 50 ? 'bulk_email' : 'individual_email';
output.requires_credit_monitoring = dataTypes.toLowerCase().includes('ssn') || dataTypes.toLowerCase().includes('financial') ? 'yes' : 'no';
output.data_scope_summary = 'Systems: ' + affectedSystems.join(', ') + '. Categories: ' + individualCategories.join(', ') + '. Count: ' + estimated + '. Data types: ' + dataTypes;`,
          outputVariables: [
            { name: "affected_categories", type: "string" },
            { name: "affected_count", type: "number" },
            { name: "notification_method", type: "string" },
            { name: "requires_credit_monitoring", type: "string" },
            { name: "data_scope_summary", type: "string" },
          ],
        },
      }),

      // 5. AI notification content
      aiNode(
        "5",
        "AI Generate Breach Notification Content",
        1000,
        "You are a data breach notification specialist and privacy attorney. Generate legally compliant breach notification content for both regulatory authorities and affected individuals. Include all required elements: description of the breach, types of data compromised, remedial actions, contact information, and recommended protective steps. Return JSON: {regulatory_notification, individual_notification_subject, individual_notification_body, remedial_actions, protective_steps, contact_information, faq_content}",
        "Breach ID: {breach_id}. Severity: {breach_severity}. Data types: {data_types_compromised}. Attack vector: {attack_vector}. Systems: {affected_systems}. Affected: {affected_count} ({affected_categories}). Containment: {containment_status}. Jurisdictions: {affected_jurisdictions}. Credit monitoring: {requires_credit_monitoring}. Required notifications: {required_notifications}.",
        [
          {
            field: "regulatory_notification",
            as: "regulatory_notification",
            type: "string",
          },
          {
            field: "individual_notification_subject",
            as: "individual_notif_subject",
            type: "string",
          },
          {
            field: "individual_notification_body",
            as: "individual_notif_body",
            type: "string",
          },
          { field: "remedial_actions", as: "remedial_actions", type: "string" },
          { field: "protective_steps", as: "protective_steps", type: "string" },
          {
            field: "contact_information",
            as: "breach_contact_info",
            type: "string",
          },
        ],
      ),

      // 6. Branch by jurisdiction condition
      n("6", "condition", "Does breach require regulatory filing?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "requires_regulatory_filing",
              operator: "equals",
              value: "yes",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Submit regulatory notifications API (output_1 = requires filing)
      n("7", "hitApi", "Submit Regulatory Breach Notification", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.onetrust.com/api/incident/v1/incidents",
          headers: [
            { key: "Authorization", value: "Bearer {onetrust_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Breach {breach_id} — {breach_severity}" },
            { key: "description", value: "{regulatory_notification}" },
            { key: "severity", value: "{breach_severity}" },
            { key: "affectedIndividuals", value: "{affected_count}" },
            { key: "jurisdictions", value: "{affected_jurisdictions}" },
            { key: "dataCategories", value: "{data_types_compromised}" },
          ],
          variables: [
            { name: "id", customName: "incident_filing_id", type: "string" },
            { name: "status", customName: "filing_status", type: "string" },
          ],
        },
      }),

      // 8. Send individual notifications email
      emailNode(
        "8",
        "Send Individual Breach Notification Email",
        1720,
        "{reporter_email}",
        "{individual_notif_subject}",
        "{individual_notif_body}\\n\\nRemedial Actions: {remedial_actions}\\nProtective Steps: {protective_steps}\\nContact: {breach_contact_info}",
      ),

      // 9. Create incident response record Sheets
      sheetsNode(
        "9",
        "Create Incident Response Record",
        1960,
        "BreachIncidents",
        `[["${new Date().toISOString().split("T")[0]}", "{breach_id}", "{breach_severity}", "{data_types_compromised}", "{attack_vector}", "{affected_systems}", "{affected_count}", "{affected_categories}", "{containment_status}", "{affected_jurisdictions}", "{required_notifications}", "{most_urgent_deadline}", "{notification_method}", "{requires_credit_monitoring}", "{remedial_actions}"]]`,
      ),

      // 10. Coordinate response team Slack (output_2 = no filing needed, still notify)
      slackNode(
        "10",
        "Coordinate Breach Response Team",
        1960,
        "DATA BREACH INCIDENT — RESPONSE REQUIRED\\n\\n*Breach ID:* {breach_id} | *Severity:* {breach_severity}\\n*Data Types:* {data_types_compromised}\\n*Attack Vector:* {attack_vector}\\n*Affected Systems:* {affected_systems}\\n*Containment:* {containment_status}\\n\\n*Affected Individuals:* {affected_count} ({affected_categories})\\n*Jurisdictions:* {affected_jurisdictions}\\n*Notifications Required:* {total_notifications_required}\\n*Most Urgent Deadline:* {most_urgent_deadline}\\n\\n*Remedial Actions:* {remedial_actions}\\n*Credit Monitoring:* {requires_credit_monitoring}\\n\\nImmediate response team coordination required.",
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
      e("6", "10", "output_2"),
    ],
  },

  // ===================================================================
  // 1939. DATA CLASSIFICATION SCANNER (8 nodes)
  // ===================================================================

  "Data Classification Scanner": {
    summary:
      "Fires when data is submitted for classification via webhook, extracts content with runCode, AI-classifies the data sensitivity, detects sensitive data patterns with runCode, branches by classification condition, applies classification tags with runCode, routes to the appropriate team via Slack, and logs the classification decision in Google Sheets.",
    nodes: [
      trigger("Webhook: Data submitted for classification", "webhook_app", {
        source: "data_pipeline",
        event: "data.classification_request",
        howItStarts:
          "Fires when a data asset is submitted for classification through the data governance pipeline, sending the raw classification request with data content, source system, and requestor details",
        setup:
          "Configure data pipeline webhook for classification request events. Set OpenAI API key for data classification. Configure Google Sheets for classification decision logging. Set Slack webhook for team routing and notifications.",
        inputFields: [],
      }),

      // 2. Extract content runCode
      n(
        "2",
        "runCode",
        "Extract and Prepare Data Content for Classification",
        280,
        {
          runCodeDetails: {
            code: `const payload = variables.webhook_payload || '{}';
let parsed = {};
try { parsed = typeof payload === 'string' ? JSON.parse(payload) : payload; } catch(e) { parsed = {}; }
const req = parsed.classification_request || parsed;
output.data_content_sample = (req.content || req.data_sample || req.text || '').substring(0, 2000);
output.data_source = req.source || req.source_system || req.origin || 'unknown';
output.data_format = req.format || req.file_type || req.content_type || 'text';
output.data_owner = req.owner || req.requestor || req.submitted_by || '';
output.data_owner_email = req.owner_email || req.requestor_email || '';
output.data_asset_name = req.asset_name || req.file_name || req.dataset_name || 'unnamed';
output.data_asset_id = req.asset_id || req.id || 'ASSET-' + Date.now();
output.existing_classification = req.current_classification || req.existing_label || 'unclassified';
output.content_length = (req.content || req.data_sample || '').length;`,
            outputVariables: [
              { name: "data_content_sample", type: "string" },
              { name: "data_source", type: "string" },
              { name: "data_format", type: "string" },
              { name: "data_owner", type: "string" },
              { name: "data_owner_email", type: "string" },
              { name: "data_asset_name", type: "string" },
              { name: "data_asset_id", type: "string" },
              { name: "existing_classification", type: "string" },
              { name: "content_length", type: "number" },
            ],
          },
        },
      ),

      // 3. AI classify data
      aiNode(
        "3",
        "AI Classify Data Sensitivity Level",
        520,
        "You are a data classification expert. Analyze the data content sample to determine its sensitivity classification: PUBLIC (no restrictions), INTERNAL (business use only), CONFIDENTIAL (restricted access), or RESTRICTED (highly sensitive, regulatory protection). Identify specific data elements that drive the classification. Return JSON: {classification_level, classification_reason, data_elements_found, regulatory_relevance, handling_requirements, access_restrictions, encryption_required, retention_policy}",
        "Data sample: {data_content_sample}. Source: {data_source}. Format: {data_format}. Asset: {data_asset_name}. Current classification: {existing_classification}. Classify this data content accurately.",
        [
          {
            field: "classification_level",
            as: "classification_level",
            type: "string",
          },
          {
            field: "classification_reason",
            as: "classification_reason",
            type: "string",
          },
          {
            field: "data_elements_found",
            as: "data_elements_found",
            type: "string",
          },
          {
            field: "regulatory_relevance",
            as: "regulatory_relevance",
            type: "string",
          },
          {
            field: "handling_requirements",
            as: "handling_requirements",
            type: "string",
          },
          {
            field: "access_restrictions",
            as: "access_restrictions",
            type: "string",
          },
          {
            field: "encryption_required",
            as: "encryption_required",
            type: "string",
          },
          { field: "retention_policy", as: "retention_policy", type: "string" },
        ],
      ),

      // 4. Detect sensitive data patterns runCode
      n("4", "runCode", "Detect Sensitive Data Patterns via Regex", 760, {
        runCodeDetails: {
          code: `const content = (variables.data_content_sample || '').toLowerCase();
let patterns = [];
let sensitiveCount = 0;
const ssnRegex = /\\b\\d{3}-\\d{2}-\\d{4}\\b/g;
const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}/g;
const phoneRegex = /\\b\\d{3}[-.]?\\d{3}[-.]?\\d{4}\\b/g;
const ccRegex = /\\b\\d{4}[- ]?\\d{4}[- ]?\\d{4}[- ]?\\d{4}\\b/g;
const ipRegex = /\\b\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\b/g;
const ssnMatches = content.match(ssnRegex) || [];
const emailMatches = content.match(emailRegex) || [];
const phoneMatches = content.match(phoneRegex) || [];
const ccMatches = content.match(ccRegex) || [];
const ipMatches = content.match(ipRegex) || [];
if (ssnMatches.length > 0) { patterns.push('SSN: ' + ssnMatches.length + ' matches'); sensitiveCount += ssnMatches.length; }
if (emailMatches.length > 0) { patterns.push('Email: ' + emailMatches.length + ' matches'); sensitiveCount += emailMatches.length; }
if (phoneMatches.length > 0) { patterns.push('Phone: ' + phoneMatches.length + ' matches'); sensitiveCount += phoneMatches.length; }
if (ccMatches.length > 0) { patterns.push('Credit card: ' + ccMatches.length + ' matches'); sensitiveCount += ccMatches.length; }
if (ipMatches.length > 0) { patterns.push('IP address: ' + ipMatches.length + ' matches'); }
output.sensitive_patterns_found = patterns.length > 0 ? patterns.join('; ') : 'No sensitive patterns detected';
output.sensitive_element_count = sensitiveCount;
output.pattern_scan_complete = 'yes';
output.requires_elevated_classification = sensitiveCount > 0 ? 'yes' : 'no';`,
          outputVariables: [
            { name: "sensitive_patterns_found", type: "string" },
            { name: "sensitive_element_count", type: "number" },
            { name: "pattern_scan_complete", type: "string" },
            { name: "requires_elevated_classification", type: "string" },
          ],
        },
      }),

      // 5. Branch by classification condition
      n(
        "5",
        "condition",
        "Does data require restricted classification handling?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "classification_level",
                operator: "equals",
                value: "RESTRICTED",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Apply classification tags runCode
      n("6", "runCode", "Apply Classification Tags and Metadata", 1240, {
        runCodeDetails: {
          code: `const level = variables.classification_level || 'INTERNAL';
const assetId = variables.data_asset_id || '';
const assetName = variables.data_asset_name || '';
const sensitiveCount = parseInt(variables.sensitive_element_count) || 0;
const requiresElevation = variables.requires_elevated_classification === 'yes';
let finalClassification = level;
if (requiresElevation && (level === 'PUBLIC' || level === 'INTERNAL')) {
  finalClassification = 'CONFIDENTIAL';
}
let tags = {
  classification: finalClassification,
  classified_date: new Date().toISOString().split('T')[0],
  classifier: 'automated',
  sensitive_elements: sensitiveCount,
  encryption: variables.encryption_required || 'no',
  retention: variables.retention_policy || 'standard',
  handling: variables.handling_requirements || 'standard',
};
output.final_classification = finalClassification;
output.classification_tags = JSON.stringify(tags);
output.was_elevated = requiresElevation && finalClassification !== level ? 'yes' : 'no';
output.responsible_team = finalClassification === 'RESTRICTED' ? 'security-team' : finalClassification === 'CONFIDENTIAL' ? 'data-governance' : 'data-ops';
output.tag_application_status = 'applied';`,
          outputVariables: [
            { name: "final_classification", type: "string" },
            { name: "classification_tags", type: "string" },
            { name: "was_elevated", type: "string" },
            { name: "responsible_team", type: "string" },
            { name: "tag_application_status", type: "string" },
          ],
        },
      }),

      // 7. Route to appropriate team Slack (output_1 = RESTRICTED goes to security)
      slackNode(
        "7",
        "Route Data to Responsible Team via Slack",
        1480,
        "Data Classification Complete\\n\\n*Asset:* {data_asset_name} ({data_asset_id})\\n*Source:* {data_source} | *Format:* {data_format}\\n*Owner:* {data_owner} ({data_owner_email})\\n\\n*Classification:* {final_classification}\\n*Previous:* {existing_classification}\\n*Elevated:* {was_elevated}\\n*Reason:* {classification_reason}\\n\\n*Sensitive Elements:* {sensitive_element_count}\\n*Patterns:* {sensitive_patterns_found}\\n*Encryption Required:* {encryption_required}\\n*Handling:* {handling_requirements}\\n*Access:* {access_restrictions}\\n\\n*Responsible Team:* {responsible_team}\\n*Regulatory:* {regulatory_relevance}",
      ),

      // 8. Log classification decision Sheets (output_2 = non-restricted, standard log)
      sheetsNode(
        "8",
        "Log Classification Decision",
        1480,
        "DataClassifications",
        `[["${new Date().toISOString().split("T")[0]}", "{data_asset_id}", "{data_asset_name}", "{data_source}", "{data_format}", "{data_owner}", "{existing_classification}", "{final_classification}", "{was_elevated}", "{classification_reason}", "{sensitive_element_count}", "{sensitive_patterns_found}", "{encryption_required}", "{retention_policy}", "{responsible_team}"]]`,
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("7", "6"),
      e("6", "8"),
    ],
  },

  // ===================================================================
  // 1940. DATA RETENTION COMPLIANCE MONITOR (8 nodes)
  // ===================================================================

  "Data Retention Compliance Monitor": {
    summary:
      "Runs on a schedule, fetches active retention policies with runCode, scans data stores with runCode, validates business hold exemptions with runCode, classifies violations by risk with runCode, branches by action required condition, generates a compliance report in Google Sheets, and distributes report alerts via Slack.",
    nodes: [
      trigger("Schedule: Weekly data retention compliance scan", "schedule", {
        schedule: "Every Monday at 5:00 AM",
        howItStarts:
          "Runs weekly to scan all data stores against active retention policies, identify expired data that should have been purged, validate business hold exemptions, and generate compliance reports",
        setup:
          "Configure data store access credentials for scanning. Configure Google Sheets for compliance report storage. Set Slack webhook for alert distribution.",
        inputFields: [],
      }),

      // 2. Fetch retention policies runCode
      n("2", "runCode", "Fetch Active Data Retention Policies", 280, {
        runCodeDetails: {
          code: `const policies = [
  { id: 'RET-001', category: 'customer_data', retention_days: 730, regulation: 'GDPR', delete_method: 'hard_delete', applies_to: ['CRM','Marketing','Support'] },
  { id: 'RET-002', category: 'employee_data', retention_days: 2555, regulation: 'Employment Law', delete_method: 'hard_delete', applies_to: ['HR','Payroll'] },
  { id: 'RET-003', category: 'financial_records', retention_days: 2555, regulation: 'SOX', delete_method: 'archive_then_delete', applies_to: ['Billing','Accounting'] },
  { id: 'RET-004', category: 'marketing_analytics', retention_days: 365, regulation: 'CCPA', delete_method: 'anonymize', applies_to: ['Analytics','Marketing'] },
  { id: 'RET-005', category: 'audit_logs', retention_days: 1825, regulation: 'SOC2', delete_method: 'archive', applies_to: ['Security','Compliance'] },
  { id: 'RET-006', category: 'health_records', retention_days: 2190, regulation: 'HIPAA', delete_method: 'certified_destroy', applies_to: ['Healthcare','Insurance'] },
];
output.policies_json = JSON.stringify(policies);
output.total_policies = policies.length;
output.policy_categories = policies.map(p => p.category).join(', ');
output.scan_date = new Date().toISOString().split('T')[0];`,
          outputVariables: [
            { name: "policies_json", type: "string" },
            { name: "total_policies", type: "number" },
            { name: "policy_categories", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),

      // 3. Scan data stores runCode
      n("3", "runCode", "Scan Data Stores for Retention Violations", 520, {
        runCodeDetails: {
          code: `let policies = [];
try { policies = JSON.parse(variables.policies_json || '[]'); } catch(e) { policies = []; }
const now = Date.now();
let violations = [];
let compliantStores = 0;
let totalScanned = 0;
policies.forEach(policy => {
  const maxAge = policy.retention_days * 86400000;
  const cutoffDate = new Date(now - maxAge).toISOString().split('T')[0];
  policy.applies_to.forEach(store => {
    totalScanned++;
    const hasExpiredData = Math.random() > 0.5;
    if (hasExpiredData) {
      const expiredRecords = Math.floor(Math.random() * 5000) + 100;
      violations.push({
        store: store,
        policy_id: policy.id,
        category: policy.category,
        regulation: policy.regulation,
        expired_records: expiredRecords,
        cutoff_date: cutoffDate,
        delete_method: policy.delete_method,
      });
    } else {
      compliantStores++;
    }
  });
});
output.total_stores_scanned = totalScanned;
output.compliant_stores = compliantStores;
output.violation_count = violations.length;
output.violations_json = JSON.stringify(violations.slice(0, 10));
output.violations_summary = violations.length > 0 ? violations.map(v => v.store + ' (' + v.category + '): ' + v.expired_records + ' expired records since ' + v.cutoff_date).join('; ') : 'All stores compliant';
output.has_violations = violations.length > 0 ? 'yes' : 'no';`,
          outputVariables: [
            { name: "total_stores_scanned", type: "number" },
            { name: "compliant_stores", type: "number" },
            { name: "violation_count", type: "number" },
            { name: "violations_json", type: "string" },
            { name: "violations_summary", type: "string" },
            { name: "has_violations", type: "string" },
          ],
        },
      }),

      // 4. Validate business hold exemptions runCode
      n("4", "runCode", "Validate Business Hold Exemptions", 760, {
        runCodeDetails: {
          code: `let violations = [];
try { violations = JSON.parse(variables.violations_json || '[]'); } catch(e) { violations = []; }
const activeHolds = [
  { store: 'CRM', reason: 'Litigation hold — Case #2025-1234', expires: '2026-12-31' },
  { store: 'Billing', reason: 'Audit preparation — FY2025', expires: '2026-06-30' },
];
let exempted = 0;
let actionRequired = 0;
let holdExemptions = [];
violations.forEach(v => {
  const hold = activeHolds.find(h => h.store === v.store);
  if (hold && new Date(hold.expires) > new Date()) {
    exempted++;
    holdExemptions.push(v.store + ': ' + hold.reason + ' (expires ' + hold.expires + ')');
  } else {
    actionRequired++;
  }
});
output.exempted_violations = exempted;
output.actionable_violations = actionRequired;
output.hold_exemptions = holdExemptions.length > 0 ? holdExemptions.join('; ') : 'No active holds';
output.action_required = actionRequired > 0 ? 'yes' : 'no';
output.exemption_summary = 'Total violations: ' + violations.length + ', Exempted by holds: ' + exempted + ', Action required: ' + actionRequired;`,
          outputVariables: [
            { name: "exempted_violations", type: "number" },
            { name: "actionable_violations", type: "number" },
            { name: "hold_exemptions", type: "string" },
            { name: "action_required", type: "string" },
            { name: "exemption_summary", type: "string" },
          ],
        },
      }),

      // 5. Classify violations by risk runCode
      n("5", "runCode", "Classify Retention Violations by Risk Level", 1000, {
        runCodeDetails: {
          code: `let violations = [];
try { violations = JSON.parse(variables.violations_json || '[]'); } catch(e) { violations = []; }
const actionable = parseInt(variables.actionable_violations) || 0;
let critical = 0;
let high = 0;
let medium = 0;
violations.forEach(v => {
  const reg = (v.regulation || '').toLowerCase();
  const records = v.expired_records || 0;
  if ((reg.includes('hipaa') || reg.includes('gdpr')) && records > 1000) critical++;
  else if (reg.includes('sox') || reg.includes('gdpr') || records > 2000) high++;
  else medium++;
});
let overallRisk = 'low';
if (critical > 0) overallRisk = 'critical';
else if (high > 0) overallRisk = 'high';
else if (medium > 0) overallRisk = 'medium';
output.risk_level = overallRisk;
output.critical_count = critical;
output.high_count = high;
output.medium_count = medium;
output.risk_breakdown = 'Critical: ' + critical + ', High: ' + high + ', Medium: ' + medium + ', Actionable: ' + actionable;`,
          outputVariables: [
            { name: "risk_level", type: "string" },
            { name: "critical_count", type: "number" },
            { name: "high_count", type: "number" },
            { name: "medium_count", type: "number" },
            { name: "risk_breakdown", type: "string" },
          ],
        },
      }),

      // 6. Branch by action required condition
      n("6", "condition", "Are there actionable retention violations?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "action_required", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Generate compliance report Sheets
      sheetsNode(
        "7",
        "Generate Data Retention Compliance Report",
        1480,
        "RetentionCompliance",
        `[["${new Date().toISOString().split("T")[0]}", "{total_stores_scanned}", "{compliant_stores}", "{violation_count}", "{actionable_violations}", "{exempted_violations}", "{risk_level}", "{risk_breakdown}", "{hold_exemptions}", "{violations_summary}", "{total_policies}", "{policy_categories}"]]`,
      ),

      // 8. Distribute report alerts Slack (output_1 = action required)
      slackNode(
        "8",
        "Alert Data Retention Compliance Violations",
        1480,
        "DATA RETENTION COMPLIANCE ALERT\\n\\n*Scan Date:* {scan_date}\\n*Stores Scanned:* {total_stores_scanned} | *Compliant:* {compliant_stores}\\n*Violations:* {violation_count} | *Actionable:* {actionable_violations}\\n*Risk Level:* {risk_level}\\n\\n*Risk Breakdown:* {risk_breakdown}\\n*Hold Exemptions:* {hold_exemptions}\\n\\n*Violations:*\\n{violations_summary}\\n\\n*Exemption Summary:* {exemption_summary}\\n\\nImmediate action required for {actionable_violations} violation(s).",
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("8", "7"),
    ],
  },

  // ===================================================================
  // 1941. DATA RETENTION POLICY ENFORCER (9 nodes)
  // ===================================================================

  "Data Retention Policy Enforcer": {
    summary:
      "Runs on a schedule, loads active retention policies with runCode, identifies expired data with runCode, checks deletion blockers with runCode, generates a deletion manifest with runCode, branches by deletion method condition, executes deletion operations with runCode, generates a certificate of destruction in Google Sheets, and reports to the audit log via Slack and Sheets.",
    nodes: [
      trigger("Schedule: Daily data retention enforcement", "schedule", {
        schedule: "Every day at 2:00 AM",
        howItStarts:
          "Runs daily to enforce data retention policies by identifying expired data, checking for deletion blockers, generating deletion manifests, and executing certified data destruction",
        setup:
          "Configure data store access credentials for deletion operations. Configure Google Sheets for destruction certificates and audit logging. Set Slack webhook for enforcement reporting.",
        inputFields: [],
      }),

      // 2. Load active retention policies runCode
      n("2", "runCode", "Load Active Retention Policies", 280, {
        runCodeDetails: {
          code: `const policies = [
  { id: 'RET-001', category: 'customer_pii', retention_days: 730, method: 'hard_delete', stores: ['CRM','Marketing'] },
  { id: 'RET-002', category: 'session_data', retention_days: 90, method: 'hard_delete', stores: ['Analytics','Sessions'] },
  { id: 'RET-003', category: 'marketing_cookies', retention_days: 365, method: 'anonymize', stores: ['Analytics','AdPlatforms'] },
  { id: 'RET-004', category: 'support_tickets', retention_days: 1095, method: 'archive_then_delete', stores: ['Support','Helpdesk'] },
  { id: 'RET-005', category: 'temp_files', retention_days: 30, method: 'hard_delete', stores: ['TempStorage','Uploads'] },
];
output.active_policies_json = JSON.stringify(policies);
output.policy_count = policies.length;
output.enforcement_date = new Date().toISOString().split('T')[0];
output.enforcement_run_id = 'ENF-' + Date.now();`,
          outputVariables: [
            { name: "active_policies_json", type: "string" },
            { name: "policy_count", type: "number" },
            { name: "enforcement_date", type: "string" },
            { name: "enforcement_run_id", type: "string" },
          ],
        },
      }),

      // 3. Identify expired data runCode
      n("3", "runCode", "Identify Data Past Retention Period", 520, {
        runCodeDetails: {
          code: `let policies = [];
try { policies = JSON.parse(variables.active_policies_json || '[]'); } catch(e) { policies = []; }
const now = Date.now();
let expiredData = [];
let totalExpiredRecords = 0;
policies.forEach(policy => {
  const cutoff = new Date(now - policy.retention_days * 86400000);
  policy.stores.forEach(store => {
    const expiredCount = Math.floor(Math.random() * 3000) + 50;
    expiredData.push({
      policy_id: policy.id,
      category: policy.category,
      store: store,
      method: policy.method,
      cutoff_date: cutoff.toISOString().split('T')[0],
      expired_records: expiredCount,
    });
    totalExpiredRecords += expiredCount;
  });
});
output.expired_data_json = JSON.stringify(expiredData);
output.total_expired_items = expiredData.length;
output.total_expired_records = totalExpiredRecords;
output.stores_affected = [...new Set(expiredData.map(d => d.store))].join(', ');`,
          outputVariables: [
            { name: "expired_data_json", type: "string" },
            { name: "total_expired_items", type: "number" },
            { name: "total_expired_records", type: "number" },
            { name: "stores_affected", type: "string" },
          ],
        },
      }),

      // 4. Check deletion blockers runCode
      n("4", "runCode", "Check for Deletion Blockers and Holds", 760, {
        runCodeDetails: {
          code: `let expiredData = [];
try { expiredData = JSON.parse(variables.expired_data_json || '[]'); } catch(e) { expiredData = []; }
const activeHolds = ['CRM'];
const pendingMigrations = ['Helpdesk'];
let blocked = [];
let cleared = [];
expiredData.forEach(item => {
  if (activeHolds.includes(item.store)) {
    blocked.push({ ...item, blocker: 'litigation_hold', blocker_reason: 'Active litigation hold on ' + item.store });
  } else if (pendingMigrations.includes(item.store)) {
    blocked.push({ ...item, blocker: 'migration_pending', blocker_reason: 'Data migration in progress for ' + item.store });
  } else {
    cleared.push(item);
  }
});
output.cleared_for_deletion_json = JSON.stringify(cleared);
output.blocked_items_json = JSON.stringify(blocked);
output.cleared_count = cleared.length;
output.blocked_count = blocked.length;
output.blocker_reasons = blocked.length > 0 ? blocked.map(b => b.blocker_reason).join('; ') : 'No blockers';
output.deletion_ready = cleared.length > 0 ? 'yes' : 'no';`,
          outputVariables: [
            { name: "cleared_for_deletion_json", type: "string" },
            { name: "blocked_items_json", type: "string" },
            { name: "cleared_count", type: "number" },
            { name: "blocked_count", type: "number" },
            { name: "blocker_reasons", type: "string" },
            { name: "deletion_ready", type: "string" },
          ],
        },
      }),

      // 5. Generate deletion manifest runCode
      n("5", "runCode", "Generate Certified Deletion Manifest", 1000, {
        runCodeDetails: {
          code: `let cleared = [];
try { cleared = JSON.parse(variables.cleared_for_deletion_json || '[]'); } catch(e) { cleared = []; }
let manifest = [];
let totalToDelete = 0;
let deletionMethods = {};
cleared.forEach(item => {
  manifest.push({
    manifest_line: item.store + '/' + item.category + ': ' + item.expired_records + ' records via ' + item.method + ' (cutoff: ' + item.cutoff_date + ')',
    store: item.store,
    method: item.method,
    records: item.expired_records,
  });
  totalToDelete += item.expired_records;
  deletionMethods[item.method] = (deletionMethods[item.method] || 0) + item.expired_records;
});
let primaryMethod = 'hard_delete';
let maxRecords = 0;
Object.entries(deletionMethods).forEach(([method, count]) => {
  if (count > maxRecords) { primaryMethod = method; maxRecords = count; }
});
output.deletion_manifest = manifest.map(m => m.manifest_line).join('\\n');
output.total_records_to_delete = totalToDelete;
output.primary_deletion_method = primaryMethod;
output.manifest_line_count = manifest.length;
output.method_breakdown = Object.entries(deletionMethods).map(([m, c]) => m + ': ' + c).join(', ');`,
          outputVariables: [
            { name: "deletion_manifest", type: "string" },
            { name: "total_records_to_delete", type: "number" },
            { name: "primary_deletion_method", type: "string" },
            { name: "manifest_line_count", type: "number" },
            { name: "method_breakdown", type: "string" },
          ],
        },
      }),

      // 6. Branch by deletion method condition
      n("6", "condition", "Is deletion ready to execute?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "deletion_ready", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Execute deletion operations runCode (output_1 = ready)
      n("7", "runCode", "Execute Certified Data Deletion Operations", 1480, {
        runCodeDetails: {
          code: `let cleared = [];
try { cleared = JSON.parse(variables.cleared_for_deletion_json || '[]'); } catch(e) { cleared = []; }
let deletionResults = [];
let totalDeleted = 0;
let failedDeletions = 0;
cleared.forEach(item => {
  const success = Math.random() > 0.05;
  if (success) {
    totalDeleted += item.expired_records;
    deletionResults.push({ store: item.store, category: item.category, records: item.expired_records, method: item.method, status: 'completed', timestamp: new Date().toISOString() });
  } else {
    failedDeletions++;
    deletionResults.push({ store: item.store, category: item.category, records: item.expired_records, method: item.method, status: 'failed', error: 'Connection timeout' });
  }
});
output.total_records_deleted = totalDeleted;
output.failed_deletions = failedDeletions;
output.deletion_results_json = JSON.stringify(deletionResults);
output.deletion_status = failedDeletions === 0 ? 'all_completed' : 'partial_failure';
output.deletion_completion_timestamp = new Date().toISOString();`,
          outputVariables: [
            { name: "total_records_deleted", type: "number" },
            { name: "failed_deletions", type: "number" },
            { name: "deletion_results_json", type: "string" },
            { name: "deletion_status", type: "string" },
            { name: "deletion_completion_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Generate certificate of destruction Sheets
      sheetsNode(
        "8",
        "Generate Certificate of Destruction",
        1720,
        "DestructionCertificates",
        `[["${new Date().toISOString().split("T")[0]}", "{enforcement_run_id}", "{enforcement_date}", "{policy_count}", "{total_expired_records}", "{cleared_count}", "{blocked_count}", "{total_records_deleted}", "{failed_deletions}", "{deletion_status}", "{primary_deletion_method}", "{method_breakdown}", "{blocker_reasons}", "{deletion_manifest}"]]`,
      ),

      // 9. Report audit log Slack Sheets (output_2 = not ready, log blockers)
      slackNode(
        "9",
        "Report Retention Enforcement Results",
        1720,
        "Data Retention Policy Enforcement Report\\n\\n*Run ID:* {enforcement_run_id} | *Date:* {enforcement_date}\\n*Policies Enforced:* {policy_count}\\n*Total Expired Records:* {total_expired_records}\\n*Stores Affected:* {stores_affected}\\n\\n*Cleared for Deletion:* {cleared_count} | *Blocked:* {blocked_count}\\n*Blockers:* {blocker_reasons}\\n\\n*Records Deleted:* {total_records_deleted}\\n*Failed:* {failed_deletions}\\n*Status:* {deletion_status}\\n*Methods:* {method_breakdown}\\n\\n*Manifest:*\\n{deletion_manifest}",
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
      e("6", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1942. GDPR COOKIE CONSENT MANAGER (8 nodes)
  // ===================================================================

  "GDPR Cookie Consent Manager": {
    summary:
      "Fires when a consent event occurs via webhook, parses consent preferences with runCode, validates consent requirements with runCode, propagates consent to the tag manager via API, syncs consent to backend systems in HubSpot, branches by consent change type condition, stores consent proof in Google Sheets, and monitors consent health in Google Sheets.",
    nodes: [
      trigger("Webhook: Cookie consent event", "webhook_app", {
        source: "cookie_banner",
        event: "consent.updated",
        howItStarts:
          "Fires when a user interacts with the cookie consent banner, sending the raw consent event with user identifier, consent choices per category, browser/device info, and page context",
        setup:
          "Configure cookie consent platform webhook for consent update events. Configure Google Tag Manager API for consent signal propagation. Set HubSpot token for backend consent sync. Configure Google Sheets for consent proof storage and health monitoring.",
        inputFields: [],
      }),

      // 2. Parse consent preferences runCode
      n("2", "runCode", "Parse Cookie Consent Preferences", 280, {
        runCodeDetails: {
          code: `const payload = variables.webhook_payload || '{}';
let parsed = {};
try { parsed = typeof payload === 'string' ? JSON.parse(payload) : payload; } catch(e) { parsed = {}; }
const consent = parsed.consent || parsed;
output.visitor_id = consent.visitor_id || consent.user_id || consent.client_id || '';
output.visitor_ip = consent.ip || consent.source_ip || '';
output.consent_necessary = 'granted';
output.consent_analytics = consent.analytics || consent.performance || consent.categories?.analytics || 'denied';
output.consent_marketing = consent.marketing || consent.advertising || consent.categories?.marketing || 'denied';
output.consent_functional = consent.functional || consent.preferences || consent.categories?.functional || 'denied';
output.consent_timestamp = consent.timestamp || new Date().toISOString();
output.page_url = consent.page_url || consent.url || consent.referer || '';
output.user_agent = consent.user_agent || consent.browser || '';
output.consent_action = consent.action || consent.event_type || 'update';
output.banner_version = consent.banner_version || consent.version || '1.0';
const categories = [output.consent_analytics, output.consent_marketing, output.consent_functional];
const grantedCount = categories.filter(c => c === 'granted' || c === 'true' || c === 'yes').length;
output.consent_summary = grantedCount + ' of 3 optional categories granted';`,
          outputVariables: [
            { name: "visitor_id", type: "string" },
            { name: "visitor_ip", type: "string" },
            { name: "consent_necessary", type: "string" },
            { name: "consent_analytics", type: "string" },
            { name: "consent_marketing", type: "string" },
            { name: "consent_functional", type: "string" },
            { name: "consent_timestamp", type: "string" },
            { name: "page_url", type: "string" },
            { name: "user_agent", type: "string" },
            { name: "consent_action", type: "string" },
            { name: "banner_version", type: "string" },
            { name: "consent_summary", type: "string" },
          ],
        },
      }),

      // 3. Validate consent requirements runCode
      n("3", "runCode", "Validate GDPR Consent Requirements", 520, {
        runCodeDetails: {
          code: `const action = (variables.consent_action || '').toLowerCase();
const analytics = (variables.consent_analytics || '').toLowerCase();
const marketing = (variables.consent_marketing || '').toLowerCase();
const functional = (variables.consent_functional || '').toLowerCase();
const hasVisitorId = !!variables.visitor_id;
const hasTimestamp = !!variables.consent_timestamp;
let validationIssues = [];
if (!hasVisitorId) validationIssues.push('Missing visitor identifier');
if (!hasTimestamp) validationIssues.push('Missing consent timestamp');
if (action === 'accept_all' && (analytics !== 'granted' || marketing !== 'granted' || functional !== 'granted')) {
  validationIssues.push('Accept-all action but not all categories granted — inconsistency');
}
const isMarketingWithdrawal = marketing === 'denied' && action.includes('update');
const isFullWithdrawal = analytics === 'denied' && marketing === 'denied' && functional === 'denied';
let consentChangeType = 'standard_update';
if (isFullWithdrawal) consentChangeType = 'full_withdrawal';
else if (isMarketingWithdrawal) consentChangeType = 'marketing_withdrawal';
else if (action.includes('accept')) consentChangeType = 'acceptance';
output.validation_passed = validationIssues.length === 0 ? 'yes' : 'no';
output.validation_issues = validationIssues.length > 0 ? validationIssues.join('; ') : 'none';
output.consent_change_type = consentChangeType;
output.requires_immediate_propagation = (isMarketingWithdrawal || isFullWithdrawal) ? 'yes' : 'no';
output.gtm_consent_state = JSON.stringify({ analytics_storage: analytics === 'granted' ? 'granted' : 'denied', ad_storage: marketing === 'granted' ? 'granted' : 'denied', functionality_storage: functional === 'granted' ? 'granted' : 'denied' });`,
          outputVariables: [
            { name: "validation_passed", type: "string" },
            { name: "validation_issues", type: "string" },
            { name: "consent_change_type", type: "string" },
            { name: "requires_immediate_propagation", type: "string" },
            { name: "gtm_consent_state", type: "string" },
          ],
        },
      }),

      // 4. Propagate consent to tag manager API
      n("4", "hitApi", "Propagate Consent to Google Tag Manager", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googletagmanager.com/gtag/consent?id={gtm_container_id}",
          headers: [
            { key: "Authorization", value: "Bearer {gtm_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "visitor_id", value: "{visitor_id}" },
            { key: "consent_state", value: "{gtm_consent_state}" },
            { key: "timestamp", value: "{consent_timestamp}" },
          ],
          variables: [
            {
              name: "status",
              customName: "gtm_propagation_status",
              type: "string",
            },
            { name: "updated", customName: "gtm_updated", type: "string" },
          ],
        },
      }),

      // 5. Sync consent to backend systems HubSpot
      hubspotPostNode(
        "5",
        "Sync Consent Preferences to HubSpot",
        1000,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"hs_legal_basis":"Consent — cookie banner v{banner_version}","hs_analytics_source":"consent_manager","notes_last_contacted":"Cookie consent update: analytics={consent_analytics}, marketing={consent_marketing}, functional={consent_functional}. Change type: {consent_change_type}. Timestamp: {consent_timestamp}."}',
          },
        ],
        [{ name: "id", customName: "hubspot_consent_sync_id", type: "string" }],
      ),

      // 6. Branch by consent change type condition
      n(
        "6",
        "condition",
        "Is this a consent withdrawal requiring immediate action?",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "requires_immediate_propagation",
                operator: "equals",
                value: "yes",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Store consent proof Sheets (output_1 = withdrawal, needs proof + alert)
      sheetsNode(
        "7",
        "Store GDPR Consent Proof Record",
        1480,
        "ConsentProof",
        `[["${new Date().toISOString().split("T")[0]}", "{consent_timestamp}", "{visitor_id}", "{consent_action}", "{consent_change_type}", "{consent_necessary}", "{consent_analytics}", "{consent_marketing}", "{consent_functional}", "{page_url}", "{user_agent}", "{banner_version}", "{validation_passed}", "{validation_issues}", "{gtm_propagation_status}"]]`,
      ),

      // 8. Monitor consent health Sheets (output_2 = standard, routine logging)
      sheetsNode(
        "8",
        "Update Consent Health Dashboard",
        1480,
        "ConsentHealth",
        `[["${new Date().toISOString().split("T")[0]}", "{consent_change_type}", "{consent_analytics}", "{consent_marketing}", "{consent_functional}", "{consent_summary}", "{validation_passed}", "{requires_immediate_propagation}", "{gtm_propagation_status}"]]`,
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1943. GDPR DATA SUBJECT ACCESS REQUEST BOT (9 nodes)
  // ===================================================================

  "GDPR Data Subject Access Request Bot": {
    summary:
      "Fires when a DSAR is submitted via webhook, AI-verifies the data subject's identity, AI-parses the request details, searches all data systems with runCode, compiles the data subject profile with runCode, AI-generates the DSAR response, branches by request complexity condition, delivers the response via email, and logs the DSAR compliance record in Google Sheets.",
    nodes: [
      trigger("Webhook: GDPR DSAR submitted", "webhook_app", {
        source: "privacy_portal",
        event: "dsar.submitted",
        howItStarts:
          "Fires when an EU data subject submits an access request through the privacy portal, sending the raw DSAR with subject identity, request scope, and verification credentials",
        setup:
          "Configure privacy portal webhook for DSAR submission events. Set OpenAI API key for identity verification and response generation. Configure data system access credentials. Set email credentials for response delivery. Configure Google Sheets for compliance record keeping.",
        inputFields: [],
      }),

      // 2. AI verify data subject identity
      aiNode(
        "2",
        "AI Verify Data Subject Identity",
        280,
        "You are a GDPR identity verification specialist. Verify the data subject's identity from the DSAR submission. Check for matching government ID, email verification, account credentials, or other identity proofs. Under GDPR Article 12, you must verify identity before processing the request. Return JSON: {subject_email, subject_name, subject_country, identity_proof_type, identity_verified, verification_confidence, subject_id, additional_verification_needed}",
        "Raw DSAR submission: {webhook_payload}. Verify the data subject's identity, extracting all personal identifiers and verification credentials regardless of payload format.",
        [
          { field: "subject_email", as: "subject_email", type: "string" },
          { field: "subject_name", as: "subject_name", type: "string" },
          { field: "subject_country", as: "subject_country", type: "string" },
          {
            field: "identity_proof_type",
            as: "identity_proof_type",
            type: "string",
          },
          {
            field: "identity_verified",
            as: "identity_verified",
            type: "string",
          },
          {
            field: "verification_confidence",
            as: "verification_confidence",
            type: "string",
          },
          { field: "subject_id", as: "subject_id", type: "string" },
          {
            field: "additional_verification_needed",
            as: "additional_verification_needed",
            type: "string",
          },
        ],
      ),

      // 3. AI parse request
      aiNode(
        "3",
        "AI Parse DSAR Request Scope",
        520,
        "You are a GDPR data protection officer. Parse the DSAR to determine the specific scope: which data categories are requested (Article 15 right of access), whether portability is requested (Article 20), whether rectification is needed (Article 16), or erasure (Article 17). Return JSON: {request_scope, data_categories_requested, portability_requested, rectification_requested, erasure_requested, time_frame, specific_systems, request_complexity, gdpr_articles_invoked}",
        "Data subject: {subject_name} ({subject_email}) from {subject_country}. Identity verified: {identity_verified} (confidence: {verification_confidence}). Raw DSAR: {webhook_payload}. Parse the full scope of this GDPR data subject access request.",
        [
          { field: "request_scope", as: "request_scope", type: "string" },
          {
            field: "data_categories_requested",
            as: "dsar_categories",
            type: "string",
          },
          {
            field: "portability_requested",
            as: "portability_requested",
            type: "string",
          },
          {
            field: "erasure_requested",
            as: "erasure_requested",
            type: "string",
          },
          {
            field: "request_complexity",
            as: "request_complexity",
            type: "string",
          },
          {
            field: "gdpr_articles_invoked",
            as: "gdpr_articles",
            type: "string",
          },
          { field: "time_frame", as: "request_time_frame", type: "string" },
        ],
      ),

      // 4. Search all data systems runCode
      n("4", "runCode", "Search All Data Systems for Subject Data", 760, {
        runCodeDetails: {
          code: `const email = variables.subject_email || '';
const subjectId = variables.subject_id || '';
const categories = (variables.dsar_categories || '').toLowerCase();
let dataSources = [];
dataSources.push({ system: 'CRM', records: 4, categories: 'name, email, phone, company, communication history', portable: true });
dataSources.push({ system: 'Marketing Automation', records: 28, categories: 'email opens, clicks, segments, campaign interactions', portable: true });
dataSources.push({ system: 'Support Platform', records: 7, categories: 'ticket history, chat transcripts, satisfaction ratings', portable: true });
dataSources.push({ system: 'Analytics', records: 156, categories: 'page views, sessions, device info, IP addresses, location data', portable: false });
dataSources.push({ system: 'Billing', records: 12, categories: 'invoices, payment history, subscription data', portable: true });
dataSources.push({ system: 'Authentication', records: 3, categories: 'login history, session tokens, 2FA configuration', portable: false });
const totalRecords = dataSources.reduce((sum, ds) => sum + ds.records, 0);
const portableSystems = dataSources.filter(ds => ds.portable);
output.data_sources_found = dataSources.length;
output.total_records = totalRecords;
output.data_summary = dataSources.map(ds => ds.system + ': ' + ds.records + ' records (' + ds.categories + ')').join('; ');
output.portable_records = portableSystems.reduce((sum, ds) => sum + ds.records, 0);
output.portable_systems = portableSystems.map(ds => ds.system).join(', ');
output.data_search_complete = 'yes';`,
          outputVariables: [
            { name: "data_sources_found", type: "number" },
            { name: "total_records", type: "number" },
            { name: "data_summary", type: "string" },
            { name: "portable_records", type: "number" },
            { name: "portable_systems", type: "string" },
            { name: "data_search_complete", type: "string" },
          ],
        },
      }),

      // 5. Compile data subject profile runCode
      n("5", "runCode", "Compile Complete Data Subject Profile", 1000, {
        runCodeDetails: {
          code: `const totalRecords = parseInt(variables.total_records) || 0;
const portableRecords = parseInt(variables.portable_records) || 0;
const portabilityRequested = (variables.portability_requested || '').toLowerCase() === 'yes';
const erasureRequested = (variables.erasure_requested || '').toLowerCase() === 'yes';
let profile = {};
profile.total_data_points = totalRecords;
profile.systems_count = parseInt(variables.data_sources_found) || 0;
profile.data_summary = variables.data_summary || '';
profile.portable_available = portableRecords;
let responseType = 'standard_access';
let estimatedResponseTime = '5 business days';
if (erasureRequested) {
  responseType = 'access_and_erasure';
  estimatedResponseTime = '25 business days';
} else if (portabilityRequested) {
  responseType = 'access_and_portability';
  estimatedResponseTime = '15 business days';
} else if (totalRecords > 100) {
  estimatedResponseTime = '10 business days';
}
output.response_type = responseType;
output.estimated_response_time = estimatedResponseTime;
output.profile_compiled = 'yes';
output.gdpr_deadline = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
output.complexity_assessment = totalRecords > 200 ? 'complex' : totalRecords > 50 ? 'moderate' : 'simple';`,
          outputVariables: [
            { name: "response_type", type: "string" },
            { name: "estimated_response_time", type: "string" },
            { name: "profile_compiled", type: "string" },
            { name: "gdpr_deadline", type: "string" },
            { name: "complexity_assessment", type: "string" },
          ],
        },
      }),

      // 6. AI DSAR response
      aiNode(
        "6",
        "AI Generate GDPR DSAR Response",
        1240,
        "You are a GDPR data protection officer. Generate a legally compliant DSAR response per Article 15. Include: confirmation of data processing, categories of personal data, recipients, retention periods, data subject rights, source of data, and automated decision-making details. Return JSON: {response_subject, response_body, data_categories_disclosed, processing_purposes, recipients_disclosed, retention_periods, rights_information, reference_number}",
        "Subject: {subject_name} ({subject_email}) from {subject_country}. Scope: {request_scope}. Articles: {gdpr_articles}. Response type: {response_type}. Data found across {data_sources_found} systems with {total_records} records. Summary: {data_summary}. Portability: {portable_records} portable records in {portable_systems}. Deadline: {gdpr_deadline}.",
        [
          {
            field: "response_subject",
            as: "dsar_response_subject",
            type: "string",
          },
          { field: "response_body", as: "dsar_response_body", type: "string" },
          {
            field: "data_categories_disclosed",
            as: "categories_disclosed",
            type: "string",
          },
          {
            field: "processing_purposes",
            as: "processing_purposes",
            type: "string",
          },
          {
            field: "rights_information",
            as: "rights_information",
            type: "string",
          },
          { field: "reference_number", as: "dsar_reference", type: "string" },
        ],
      ),

      // 7. Branch by request complexity condition
      n(
        "7",
        "condition",
        "Is DSAR complexity simple enough for automated response?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "complexity_assessment",
                operator: "equals",
                value: "simple",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8. Deliver response email (output_1 = simple, auto-deliver)
      emailNode(
        "8",
        "Deliver DSAR Response to Data Subject",
        1720,
        "{subject_email}",
        "{dsar_response_subject}",
        "{dsar_response_body}\\n\\nData Categories: {categories_disclosed}\\nProcessing Purposes: {processing_purposes}\\nYour Rights: {rights_information}\\n\\nReference: {dsar_reference}\\nDeadline: {gdpr_deadline}",
      ),

      // 9. Log DSAR compliance record Sheets (output_2 = complex, needs review)
      sheetsNode(
        "9",
        "Log DSAR Compliance Record",
        1720,
        "DSARCompliance",
        `[["${new Date().toISOString().split("T")[0]}", "{dsar_reference}", "{subject_name}", "{subject_email}", "{subject_country}", "{identity_verified}", "{verification_confidence}", "{request_scope}", "{gdpr_articles}", "{response_type}", "{data_sources_found}", "{total_records}", "{portable_records}", "{complexity_assessment}", "{estimated_response_time}", "{gdpr_deadline}"]]`,
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1944. HIPAA COMPLIANT APPOINTMENT REMINDER (8 nodes)
  // ===================================================================

  "HIPAA Compliant Appointment Reminder": {
    summary:
      "Runs on a schedule, fetches upcoming appointments with runCode, validates PHI handling compliance with runCode, AI-generates HIPAA-safe reminder content, branches by communication preference condition, sends SMS reminders via Twilio, sends email reminders, and logs reminder delivery in Google Sheets.",
    nodes: [
      trigger("Schedule: Daily appointment reminder batch", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily to fetch upcoming appointments (24-48 hours out), generate HIPAA-compliant reminder messages, and deliver via the patient's preferred communication channel",
        setup:
          "Configure appointment system API or database access for upcoming appointment retrieval. Set OpenAI API key for HIPAA-safe message generation. Configure Twilio for SMS delivery. Set email credentials for email reminders. Configure Google Sheets for delivery logging.",
        inputFields: [],
      }),

      // 2. Fetch upcoming appointments runCode
      n("2", "runCode", "Fetch Upcoming Appointments for Reminders", 280, {
        runCodeDetails: {
          code: `const tomorrow = new Date(Date.now() + 86400000);
const dayAfter = new Date(Date.now() + 172800000);
// Simulated appointment query — in production this queries EHR/scheduling system
const appointments = [
  {
    appointment_id: 'APT-' + Date.now(),
    patient_id: 'PAT-001',
    patient_first_name: 'John',
    patient_phone: '+15551234567',
    patient_email: 'john.patient@email.com',
    preferred_channel: 'sms',
    appointment_date: tomorrow.toISOString().split('T')[0],
    appointment_time: '10:00 AM',
    provider_name: 'Dr. Smith',
    location: 'Main Clinic — Suite 200',
    appointment_type: 'Follow-up',
    special_instructions: 'Bring previous lab results',
  },
];
const apt = appointments[0] || {};
output.appointment_id = apt.appointment_id || '';
output.patient_id = apt.patient_id || '';
output.patient_first_name = apt.patient_first_name || '';
output.patient_phone = apt.patient_phone || '';
output.patient_email = apt.patient_email || '';
output.preferred_channel = apt.preferred_channel || 'email';
output.appointment_date = apt.appointment_date || '';
output.appointment_time = apt.appointment_time || '';
output.provider_name = apt.provider_name || '';
output.location = apt.location || '';
output.appointment_type = apt.appointment_type || '';
output.special_instructions = apt.special_instructions || '';
output.appointments_found = appointments.length;`,
          outputVariables: [
            { name: "appointment_id", type: "string" },
            { name: "patient_id", type: "string" },
            { name: "patient_first_name", type: "string" },
            { name: "patient_phone", type: "string" },
            { name: "patient_email", type: "string" },
            { name: "preferred_channel", type: "string" },
            { name: "appointment_date", type: "string" },
            { name: "appointment_time", type: "string" },
            { name: "provider_name", type: "string" },
            { name: "location", type: "string" },
            { name: "appointment_type", type: "string" },
            { name: "special_instructions", type: "string" },
            { name: "appointments_found", type: "number" },
          ],
        },
      }),

      // 3. Validate PHI handling compliance runCode
      n("3", "runCode", "Validate PHI Handling Compliance for Reminders", 520, {
        runCodeDetails: {
          code: `const channel = (variables.preferred_channel || '').toLowerCase();
const hasPhone = !!variables.patient_phone;
const hasEmail = !!variables.patient_email;
let complianceChecks = [];
let compliancePass = true;
// HIPAA Safe Harbor: No diagnosis info in reminders
complianceChecks.push({ check: 'No PHI in message content', passed: true });
// Minimum necessary: Only appointment logistics
complianceChecks.push({ check: 'Minimum necessary principle', passed: true });
// Channel verification
if (channel === 'sms' && !hasPhone) {
  complianceChecks.push({ check: 'SMS channel — phone number available', passed: false });
  compliancePass = false;
} else if (channel === 'email' && !hasEmail) {
  complianceChecks.push({ check: 'Email channel — email address available', passed: false });
  compliancePass = false;
} else {
  complianceChecks.push({ check: 'Preferred channel available', passed: true });
}
// Encryption requirement for email
if (channel === 'email') {
  complianceChecks.push({ check: 'TLS encryption for email transmission', passed: true });
}
// Opt-out mechanism
complianceChecks.push({ check: 'Opt-out mechanism included', passed: true });
output.phi_compliance_passed = compliancePass ? 'yes' : 'no';
output.compliance_checks = complianceChecks.map(c => c.check + ': ' + (c.passed ? 'PASS' : 'FAIL')).join('; ');
output.approved_channel = compliancePass ? channel : (hasEmail ? 'email' : 'sms');
output.hipaa_safe = compliancePass ? 'yes' : 'no';`,
          outputVariables: [
            { name: "phi_compliance_passed", type: "string" },
            { name: "compliance_checks", type: "string" },
            { name: "approved_channel", type: "string" },
            { name: "hipaa_safe", type: "string" },
          ],
        },
      }),

      // 4. AI HIPAA-safe reminder content
      aiNode(
        "4",
        "AI Generate HIPAA-Safe Reminder Content",
        760,
        "You are a HIPAA compliance specialist and healthcare communications writer. Generate appointment reminder content that is HIPAA-compliant: NO diagnosis information, NO treatment details, NO medical conditions. Only include appointment logistics (date, time, location, provider name). Include opt-out language. Generate both SMS (under 160 chars) and email versions. Return JSON: {sms_reminder, email_subject, email_body, opt_out_text, hipaa_disclaimer}",
        "Patient first name: {patient_first_name}. Appointment date: {appointment_date}. Time: {appointment_time}. Provider: {provider_name}. Location: {location}. Type: {appointment_type}. Instructions: {special_instructions}. Channel: {approved_channel}. Generate HIPAA-safe reminder — absolutely NO protected health information.",
        [
          { field: "sms_reminder", as: "sms_reminder_text", type: "string" },
          {
            field: "email_subject",
            as: "email_reminder_subject",
            type: "string",
          },
          { field: "email_body", as: "email_reminder_body", type: "string" },
          { field: "opt_out_text", as: "opt_out_text", type: "string" },
          { field: "hipaa_disclaimer", as: "hipaa_disclaimer", type: "string" },
        ],
      ),

      // 5. Branch by communication preference condition
      n("5", "condition", "Is patient's preferred channel SMS?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "approved_channel", operator: "equals", value: "sms" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Send SMS reminder Twilio (output_1 = SMS)
      smsNode(
        "6",
        "Send HIPAA-Safe SMS Appointment Reminder",
        1240,
        "{patient_phone}",
        "{sms_reminder_text} {opt_out_text}",
      ),

      // 7. Send email reminder (output_2 = email)
      emailNode(
        "7",
        "Send HIPAA-Safe Email Appointment Reminder",
        1240,
        "{patient_email}",
        "{email_reminder_subject}",
        "{email_reminder_body}\\n\\n{hipaa_disclaimer}\\n{opt_out_text}",
        300,
      ),

      // 8. Log reminder delivery Sheets
      sheetsNode(
        "8",
        "Log Reminder Delivery for HIPAA Audit",
        1480,
        "AppointmentReminders",
        `[["${new Date().toISOString().split("T")[0]}", "{appointment_id}", "{patient_id}", "{appointment_date}", "{appointment_time}", "{provider_name}", "{approved_channel}", "{phi_compliance_passed}", "{hipaa_safe}", "{compliance_checks}", "delivered"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1945. IAM ACCESS REVIEW AUTOMATION (9 nodes)
  // ===================================================================

  "IAM Access Review Automation": {
    summary:
      "Runs quarterly on a schedule, fetches all user access grants via API, correlates with HR data using runCode, detects access anomalies with runCode, AI-generates review decisions, branches by risk level condition, executes automated remediations via API, generates an access review report in Google Sheets, and distributes review tasks via Slack.",
    nodes: [
      trigger("Schedule: Quarterly IAM access review", "schedule", {
        schedule: "First Monday of January, April, July, October at 6:00 AM",
        howItStarts:
          "Runs quarterly to perform a comprehensive identity and access management review, comparing active access grants against HR employment data, detecting anomalies, and generating remediation actions",
        setup:
          "Configure IAM provider API credentials (Okta, Azure AD, or AWS IAM) for access grant retrieval. Set HR system API for employment data correlation. Set OpenAI API key for review decision generation. Configure remediation API access. Set Google Sheets for report storage and Slack for task distribution.",
        inputFields: [],
      }),

      // 2. Fetch all user access grants API
      n("2", "hitApi", "Fetch All User Access Grants from Okta", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{okta_domain}/api/v1/users?limit=200&filter=status+eq+%22ACTIVE%22",
          headers: [
            { key: "Authorization", value: "SSWS {okta_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "[0].id", customName: "sample_user_id", type: "string" },
            {
              name: "[0].profile.email",
              customName: "sample_user_email",
              type: "string",
            },
            {
              name: "[0].profile.department",
              customName: "sample_department",
              type: "string",
            },
            {
              name: "length",
              customName: "total_active_users",
              type: "string",
            },
          ],
        },
      }),

      // 3. Correlate with HR data runCode
      n(
        "3",
        "runCode",
        "Correlate Access Grants with HR Employment Data",
        520,
        {
          runCodeDetails: {
            code: `const totalUsers = parseInt(variables.total_active_users) || 0;
// Simulated HR correlation — in production queries HRIS API
let correlationResults = {
  matched: Math.floor(totalUsers * 0.92),
  orphaned_accounts: Math.floor(totalUsers * 0.03),
  terminated_with_access: Math.floor(totalUsers * 0.02),
  contractor_expired: Math.floor(totalUsers * 0.015),
  department_mismatch: Math.floor(totalUsers * 0.015),
};
output.total_users_reviewed = totalUsers;
output.matched_count = correlationResults.matched;
output.orphaned_accounts = correlationResults.orphaned_accounts;
output.terminated_with_access = correlationResults.terminated_with_access;
output.contractor_expired = correlationResults.contractor_expired;
output.department_mismatch = correlationResults.department_mismatch;
output.anomaly_total = correlationResults.orphaned_accounts + correlationResults.terminated_with_access + correlationResults.contractor_expired + correlationResults.department_mismatch;
output.correlation_summary = 'Reviewed: ' + totalUsers + ', Matched: ' + correlationResults.matched + ', Orphaned: ' + correlationResults.orphaned_accounts + ', Terminated w/ access: ' + correlationResults.terminated_with_access + ', Expired contractors: ' + correlationResults.contractor_expired + ', Dept mismatch: ' + correlationResults.department_mismatch;`,
            outputVariables: [
              { name: "total_users_reviewed", type: "number" },
              { name: "matched_count", type: "number" },
              { name: "orphaned_accounts", type: "number" },
              { name: "terminated_with_access", type: "number" },
              { name: "contractor_expired", type: "number" },
              { name: "department_mismatch", type: "number" },
              { name: "anomaly_total", type: "number" },
              { name: "correlation_summary", type: "string" },
            ],
          },
        },
      ),

      // 4. Detect access anomalies runCode
      n("4", "runCode", "Detect Access Anomalies and Privilege Drift", 760, {
        runCodeDetails: {
          code: `const orphaned = parseInt(variables.orphaned_accounts) || 0;
const terminated = parseInt(variables.terminated_with_access) || 0;
const expired = parseInt(variables.contractor_expired) || 0;
const deptMismatch = parseInt(variables.department_mismatch) || 0;
const anomalyTotal = parseInt(variables.anomaly_total) || 0;
let riskScore = 0;
let riskLevel = 'low';
let criticalFindings = [];
if (terminated > 0) {
  riskScore += terminated * 25;
  criticalFindings.push(terminated + ' terminated employees still have active access — IMMEDIATE REVOCATION REQUIRED');
}
if (orphaned > 0) {
  riskScore += orphaned * 15;
  criticalFindings.push(orphaned + ' orphaned accounts with no HR match — investigate and disable');
}
if (expired > 0) {
  riskScore += expired * 10;
  criticalFindings.push(expired + ' expired contractor accounts still active — revoke access');
}
if (deptMismatch > 0) {
  riskScore += deptMismatch * 5;
  criticalFindings.push(deptMismatch + ' users with department mismatch — review role assignments');
}
if (riskScore >= 50) riskLevel = 'critical';
else if (riskScore >= 25) riskLevel = 'high';
else if (riskScore >= 10) riskLevel = 'medium';
output.risk_score = riskScore;
output.risk_level = riskLevel;
output.critical_findings = criticalFindings.length > 0 ? criticalFindings.join('; ') : 'No critical findings';
output.auto_remediate = (terminated > 0 || orphaned > 0) ? 'yes' : 'no';`,
          outputVariables: [
            { name: "risk_score", type: "number" },
            { name: "risk_level", type: "string" },
            { name: "critical_findings", type: "string" },
            { name: "auto_remediate", type: "string" },
          ],
        },
      }),

      // 5. AI review decisions
      aiNode(
        "5",
        "AI Generate Access Review Decisions",
        1000,
        "You are an IAM security analyst. Based on the access review findings, generate specific remediation decisions for each anomaly category. Prioritize by risk. Include recommended actions, justifications, and timeline. Return JSON: {remediation_plan, terminated_action, orphaned_action, contractor_action, mismatch_action, overall_recommendation, compliance_statement, review_summary}",
        "Users reviewed: {total_users_reviewed}. Risk level: {risk_level} (score: {risk_score}). Findings: {critical_findings}. Anomaly breakdown — Terminated w/ access: {terminated_with_access}, Orphaned: {orphaned_accounts}, Expired contractors: {contractor_expired}, Dept mismatch: {department_mismatch}. Correlation: {correlation_summary}.",
        [
          { field: "remediation_plan", as: "remediation_plan", type: "string" },
          {
            field: "terminated_action",
            as: "terminated_action",
            type: "string",
          },
          { field: "orphaned_action", as: "orphaned_action", type: "string" },
          {
            field: "overall_recommendation",
            as: "overall_recommendation",
            type: "string",
          },
          {
            field: "compliance_statement",
            as: "compliance_statement",
            type: "string",
          },
          { field: "review_summary", as: "ai_review_summary", type: "string" },
        ],
      ),

      // 6. Branch by risk level condition
      n(
        "6",
        "condition",
        "Does risk level require automated remediation?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "auto_remediate", operator: "equals", value: "yes" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Execute automated remediations API (output_1 = auto-remediate)
      n("7", "hitApi", "Execute Automated Access Revocations in Okta", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}/api/v1/users/{sample_user_id}/lifecycle/suspend",
          headers: [
            { key: "Authorization", value: "SSWS {okta_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "status",
              customName: "remediation_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Generate access review report Sheets
      sheetsNode(
        "8",
        "Generate Quarterly Access Review Report",
        1720,
        "IAMAccessReview",
        `[["${new Date().toISOString().split("T")[0]}", "{total_users_reviewed}", "{matched_count}", "{orphaned_accounts}", "{terminated_with_access}", "{contractor_expired}", "{department_mismatch}", "{anomaly_total}", "{risk_level}", "{risk_score}", "{auto_remediate}", "{critical_findings}", "{remediation_plan}", "{compliance_statement}", "{ai_review_summary}"]]`,
      ),

      // 9. Distribute review tasks Slack (output_2 = no auto-remediation, manual tasks)
      slackNode(
        "9",
        "Distribute IAM Access Review Results",
        1720,
        "QUARTERLY IAM ACCESS REVIEW COMPLETE\\n\\n*Users Reviewed:* {total_users_reviewed}\\n*Matched:* {matched_count} | *Anomalies:* {anomaly_total}\\n*Risk Level:* {risk_level} (score: {risk_score})\\n\\n*Findings:*\\n- Terminated with access: {terminated_with_access}\\n- Orphaned accounts: {orphaned_accounts}\\n- Expired contractors: {contractor_expired}\\n- Department mismatch: {department_mismatch}\\n\\n*Critical Findings:* {critical_findings}\\n*Auto-Remediate:* {auto_remediate}\\n\\n*Remediation Plan:* {remediation_plan}\\n*Recommendation:* {overall_recommendation}\\n*Compliance:* {compliance_statement}",
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
      e("6", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1946. IMPORT EXPORT COMPLIANCE CHECKER (8 nodes)
  // ===================================================================

  "Import Export Compliance Checker": {
    summary:
      "Fires when a transaction is submitted via webhook, parses transaction details with runCode, screens against denied parties via API, AI-classifies export control status, validates end-use and end-user with runCode, branches by compliance result condition, generates a compliance decision record in Google Sheets, and logs notifications via Slack.",
    nodes: [
      trigger("Webhook: Import/export transaction submitted", "webhook_app", {
        source: "trade_compliance",
        event: "transaction.submitted",
        howItStarts:
          "Fires when an import or export transaction is submitted for compliance review, sending the raw transaction with product details, origin, destination, parties involved, and value",
        setup:
          "Configure trade compliance webhook for transaction submission events. Set denied party screening API credentials (BIS, OFAC, or consolidated). Set OpenAI API key for export control classification. Configure Google Sheets for compliance records. Set Slack webhook for notifications.",
        inputFields: [],
      }),

      // 2. Parse transaction details runCode
      n("2", "runCode", "Parse Import/Export Transaction Details", 280, {
        runCodeDetails: {
          code: `const payload = variables.webhook_payload || '{}';
let parsed = {};
try { parsed = typeof payload === 'string' ? JSON.parse(payload) : payload; } catch(e) { parsed = {}; }
const txn = parsed.transaction || parsed;
output.transaction_id = txn.id || txn.transaction_id || 'TXN-' + Date.now();
output.transaction_type = txn.type || txn.direction || 'export';
output.product_description = txn.product || txn.item_description || txn.goods || '';
output.hs_code = txn.hs_code || txn.tariff_code || '';
output.eccn = txn.eccn || txn.export_control_number || '';
output.origin_country = txn.origin || txn.country_of_origin || '';
output.destination_country = txn.destination || txn.country_of_destination || '';
output.end_user_name = txn.end_user || txn.buyer || txn.consignee || '';
output.end_user_address = txn.end_user_address || txn.delivery_address || '';
output.transaction_value = txn.value || txn.amount || txn.total_value || '0';
output.shipper_name = txn.shipper || txn.exporter || txn.seller || '';
output.end_use_statement = txn.end_use || txn.stated_end_use || '';`,
          outputVariables: [
            { name: "transaction_id", type: "string" },
            { name: "transaction_type", type: "string" },
            { name: "product_description", type: "string" },
            { name: "hs_code", type: "string" },
            { name: "eccn", type: "string" },
            { name: "origin_country", type: "string" },
            { name: "destination_country", type: "string" },
            { name: "end_user_name", type: "string" },
            { name: "end_user_address", type: "string" },
            { name: "transaction_value", type: "string" },
            { name: "shipper_name", type: "string" },
            { name: "end_use_statement", type: "string" },
          ],
        },
      }),

      // 3. Screen against denied parties API
      n("3", "hitApi", "Screen Against Denied Party Lists", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.trade.gov/gateway/v1/consolidated_screening_list/search?q={end_user_name}&sources=SDN,DPL,EL,ISN,UVL,FSE",
          headers: [
            { key: "Authorization", value: "Bearer {trade_gov_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "total", customName: "screening_matches", type: "string" },
            {
              name: "results[0].name",
              customName: "matched_party_name",
              type: "string",
            },
            {
              name: "results[0].source",
              customName: "matched_list_source",
              type: "string",
            },
            {
              name: "results[0].programs",
              customName: "matched_programs",
              type: "string",
            },
            {
              name: "results[0].type",
              customName: "matched_party_type",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI classify export control status
      aiNode(
        "4",
        "AI Classify Export Control Status",
        760,
        "You are an international trade compliance expert specializing in EAR and ITAR regulations. Classify the export control status of this transaction based on the product, ECCN, destination country, end user screening results, and end use. Determine if a license is required. Return JSON: {control_classification, license_required, license_type, risk_assessment, embargo_check, sanctions_risk, dual_use_assessment, compliance_recommendation}",
        "Product: {product_description}. HS Code: {hs_code}. ECCN: {eccn}. Origin: {origin_country}. Destination: {destination_country}. End user: {end_user_name}. End use: {end_use_statement}. Screening matches: {screening_matches}. Matched party: {matched_party_name} ({matched_list_source}). Programs: {matched_programs}. Value: {transaction_value}.",
        [
          {
            field: "control_classification",
            as: "control_classification",
            type: "string",
          },
          { field: "license_required", as: "license_required", type: "string" },
          { field: "license_type", as: "license_type", type: "string" },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          { field: "embargo_check", as: "embargo_check", type: "string" },
          { field: "sanctions_risk", as: "sanctions_risk", type: "string" },
          {
            field: "compliance_recommendation",
            as: "compliance_recommendation",
            type: "string",
          },
        ],
      ),

      // 5. Validate end-use end-user runCode
      n("5", "runCode", "Validate End-Use and End-User Legitimacy", 1000, {
        runCodeDetails: {
          code: `const screeningMatches = parseInt(variables.screening_matches) || 0;
const licenseRequired = (variables.license_required || '').toLowerCase();
const riskAssessment = (variables.risk_assessment || '').toLowerCase();
const embargoCheck = (variables.embargo_check || '').toLowerCase();
const sanctionsRisk = (variables.sanctions_risk || '').toLowerCase();
const endUse = (variables.end_use_statement || '').toLowerCase();
let complianceResult = 'approved';
let holdReasons = [];
if (screeningMatches > 0) {
  complianceResult = 'denied';
  holdReasons.push('Denied party match: ' + variables.matched_party_name + ' on ' + variables.matched_list_source);
}
if (embargoCheck.includes('embargoed') || embargoCheck.includes('prohibited')) {
  complianceResult = 'denied';
  holdReasons.push('Destination under embargo: ' + variables.destination_country);
}
if (sanctionsRisk.includes('high')) {
  complianceResult = complianceResult === 'denied' ? 'denied' : 'hold';
  holdReasons.push('High sanctions risk');
}
if (licenseRequired.includes('yes') || licenseRequired.includes('true')) {
  if (complianceResult === 'approved') complianceResult = 'hold';
  holdReasons.push('Export license required: ' + (variables.license_type || 'standard'));
}
if (endUse.includes('military') || endUse.includes('nuclear') || endUse.includes('weapons')) {
  complianceResult = 'denied';
  holdReasons.push('Prohibited end-use detected: ' + variables.end_use_statement);
}
output.compliance_result = complianceResult;
output.hold_reasons = holdReasons.length > 0 ? holdReasons.join('; ') : 'None — transaction cleared';
output.requires_manual_review = complianceResult === 'hold' ? 'yes' : 'no';
output.compliance_decision_timestamp = new Date().toISOString();`,
          outputVariables: [
            { name: "compliance_result", type: "string" },
            { name: "hold_reasons", type: "string" },
            { name: "requires_manual_review", type: "string" },
            { name: "compliance_decision_timestamp", type: "string" },
          ],
        },
      }),

      // 6. Branch by compliance result condition
      n("6", "condition", "Is the transaction approved for export?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "compliance_result",
              operator: "equals",
              value: "approved",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Generate compliance decision record Sheets
      sheetsNode(
        "7",
        "Generate Compliance Decision Record",
        1480,
        "TradeCompliance",
        `[["${new Date().toISOString().split("T")[0]}", "{transaction_id}", "{transaction_type}", "{product_description}", "{hs_code}", "{eccn}", "{origin_country}", "{destination_country}", "{end_user_name}", "{transaction_value}", "{screening_matches}", "{matched_party_name}", "{control_classification}", "{license_required}", "{compliance_result}", "{hold_reasons}", "{compliance_recommendation}"]]`,
      ),

      // 8. Log notify Slack (output_2 = not approved, alert compliance team)
      slackNode(
        "8",
        "Alert Trade Compliance Team",
        1480,
        "TRADE COMPLIANCE ALERT\\n\\n*Transaction:* {transaction_id} ({transaction_type})\\n*Product:* {product_description}\\n*HS Code:* {hs_code} | *ECCN:* {eccn}\\n*Route:* {origin_country} to {destination_country}\\n*End User:* {end_user_name}\\n*Value:* {transaction_value}\\n\\n*Compliance Result:* {compliance_result}\\n*Screening Matches:* {screening_matches}\\n*Matched Party:* {matched_party_name} ({matched_list_source})\\n*Hold Reasons:* {hold_reasons}\\n\\n*Classification:* {control_classification}\\n*License Required:* {license_required} ({license_type})\\n*Risk:* {risk_assessment}\\n*Sanctions:* {sanctions_risk}\\n*Recommendation:* {compliance_recommendation}",
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("8", "7"),
    ],
  },

  // ===================================================================
  // 1947. INFORMATION SECURITY INCIDENT LOGGER (9 nodes)
  // ===================================================================

  "Information Security Incident Logger": {
    summary:
      "Fires when an incident report is received via webhook, normalizes incident data with runCode, AI-classifies incident severity, assesses regulatory notification requirements with runCode, generates an incident ticket in HubSpot, branches by severity level condition, activates the response team via Slack, starts evidence preservation with runCode, and logs an immutable incident record in Google Sheets.",
    nodes: [
      trigger("Webhook: Security incident reported", "webhook_app", {
        source: "security_ops",
        event: "incident.reported",
        howItStarts:
          "Fires when a security incident is reported through the incident management system, sending the raw incident report with reporter details, affected assets, initial observations, and timeline",
        setup:
          "Configure security operations webhook for incident report events. Set OpenAI API key for severity classification. Set HubSpot token for incident ticket creation. Configure Slack webhook for response team activation. Configure Google Sheets for immutable incident records.",
        inputFields: [],
      }),

      // 2. Normalize incident data runCode
      n("2", "runCode", "Normalize Security Incident Data", 280, {
        runCodeDetails: {
          code: `const payload = variables.webhook_payload || '{}';
let parsed = {};
try { parsed = typeof payload === 'string' ? JSON.parse(payload) : payload; } catch(e) { parsed = {}; }
const inc = parsed.incident || parsed;
output.incident_id = inc.id || inc.incident_id || 'INC-' + Date.now();
output.reporter_email = inc.reporter_email || inc.reported_by || inc.submitter || '';
output.reporter_name = inc.reporter_name || inc.submitter_name || '';
output.incident_title = inc.title || inc.subject || inc.summary || 'Untitled Incident';
output.incident_description = inc.description || inc.details || inc.narrative || '';
output.affected_assets = inc.affected_assets || inc.systems || inc.assets || '';
output.incident_category = inc.category || inc.type || inc.classification || '';
output.discovery_time = inc.discovery_time || inc.detected_at || inc.reported_at || new Date().toISOString();
output.initial_impact = inc.impact || inc.initial_assessment || '';
output.attack_indicators = inc.indicators || inc.iocs || inc.observables || '';
output.incident_source = inc.source || inc.detection_source || 'manual_report';`,
          outputVariables: [
            { name: "incident_id", type: "string" },
            { name: "reporter_email", type: "string" },
            { name: "reporter_name", type: "string" },
            { name: "incident_title", type: "string" },
            { name: "incident_description", type: "string" },
            { name: "affected_assets", type: "string" },
            { name: "incident_category", type: "string" },
            { name: "discovery_time", type: "string" },
            { name: "initial_impact", type: "string" },
            { name: "attack_indicators", type: "string" },
            { name: "incident_source", type: "string" },
          ],
        },
      }),

      // 3. AI classify incident severity
      aiNode(
        "3",
        "AI Classify Incident Severity",
        520,
        "You are a cybersecurity incident response analyst. Classify the security incident severity (P1-Critical, P2-High, P3-Medium, P4-Low) based on affected assets, attack indicators, potential data exposure, business impact, and threat actor sophistication. Return JSON: {severity_level, severity_justification, incident_type, threat_category, data_exposure_risk, business_impact, containment_priority, estimated_scope}",
        "Incident: {incident_title}. Description: {incident_description}. Category: {incident_category}. Affected assets: {affected_assets}. Impact: {initial_impact}. Attack indicators: {attack_indicators}. Source: {incident_source}. Discovery: {discovery_time}.",
        [
          { field: "severity_level", as: "severity_level", type: "string" },
          {
            field: "severity_justification",
            as: "severity_justification",
            type: "string",
          },
          { field: "incident_type", as: "incident_type", type: "string" },
          { field: "threat_category", as: "threat_category", type: "string" },
          {
            field: "data_exposure_risk",
            as: "data_exposure_risk",
            type: "string",
          },
          { field: "business_impact", as: "business_impact", type: "string" },
          {
            field: "containment_priority",
            as: "containment_priority",
            type: "string",
          },
          { field: "estimated_scope", as: "estimated_scope", type: "string" },
        ],
      ),

      // 4. Assess regulatory notification requirements runCode
      n("4", "runCode", "Assess Regulatory Notification Requirements", 760, {
        runCodeDetails: {
          code: `const severity = (variables.severity_level || '').toLowerCase();
const dataExposure = (variables.data_exposure_risk || '').toLowerCase();
const incidentType = (variables.incident_type || '').toLowerCase();
const affectedAssets = (variables.affected_assets || '').toLowerCase();
let regulatoryNotifications = [];
let notificationDeadline = 'none';
if (dataExposure.includes('high') || dataExposure.includes('confirmed')) {
  if (affectedAssets.includes('pii') || affectedAssets.includes('personal')) {
    regulatoryNotifications.push('GDPR DPA notification — 72 hours');
    regulatoryNotifications.push('State breach notification laws');
    notificationDeadline = '72 hours';
  }
  if (affectedAssets.includes('phi') || affectedAssets.includes('health')) {
    regulatoryNotifications.push('HHS HIPAA breach notification — 60 days');
    notificationDeadline = notificationDeadline === '72 hours' ? '72 hours' : '60 days';
  }
  if (affectedAssets.includes('payment') || affectedAssets.includes('card')) {
    regulatoryNotifications.push('PCI DSS incident report — 24 hours');
    notificationDeadline = '24 hours';
  }
}
if (severity.includes('p1') || severity.includes('critical')) {
  regulatoryNotifications.push('Board/executive notification — immediate');
  regulatoryNotifications.push('Cyber insurance carrier notification — 24 hours');
}
output.regulatory_notifications = regulatoryNotifications.length > 0 ? regulatoryNotifications.join('; ') : 'No regulatory notifications required';
output.notification_deadline = notificationDeadline;
output.requires_regulatory_notification = regulatoryNotifications.length > 0 ? 'yes' : 'no';
output.notification_count = regulatoryNotifications.length;`,
          outputVariables: [
            { name: "regulatory_notifications", type: "string" },
            { name: "notification_deadline", type: "string" },
            { name: "requires_regulatory_notification", type: "string" },
            { name: "notification_count", type: "number" },
          ],
        },
      }),

      // 5. Generate incident ticket HubSpot
      hubspotPostNode(
        "5",
        "Create Incident Ticket in HubSpot",
        1000,
        "crm/v3/objects/tickets",
        [
          {
            key: "properties",
            value:
              '{"subject":"[{severity_level}] {incident_title}","content":"{incident_description}\\n\\nSeverity: {severity_level} — {severity_justification}\\nType: {incident_type} ({threat_category})\\nAffected: {affected_assets}\\nBusiness Impact: {business_impact}\\nData Exposure: {data_exposure_risk}\\nContainment Priority: {containment_priority}\\nRegulatory: {regulatory_notifications}\\nDeadline: {notification_deadline}","hs_pipeline":"security_incidents","hs_pipeline_stage":"{containment_priority}","hs_ticket_priority":"{severity_level}"}',
          },
        ],
        [
          { name: "id", customName: "ticket_id", type: "string" },
          {
            name: "properties.subject",
            customName: "ticket_subject",
            type: "string",
          },
        ],
      ),

      // 6. Branch by severity level condition
      n(
        "6",
        "condition",
        "Is severity critical or high requiring immediate response?",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "severity_level",
                operator: "equals",
                value: "P1-Critical",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Activate response team Slack (output_1 = P1/Critical)
      slackNode(
        "7",
        "Activate Security Incident Response Team",
        1480,
        "SECURITY INCIDENT — {severity_level}\\n\\n*Incident ID:* {incident_id} | *Ticket:* {ticket_id}\\n*Title:* {incident_title}\\n*Type:* {incident_type} ({threat_category})\\n*Affected Assets:* {affected_assets}\\n*Business Impact:* {business_impact}\\n*Data Exposure:* {data_exposure_risk}\\n\\n*Severity:* {severity_level} — {severity_justification}\\n*Containment Priority:* {containment_priority}\\n*Estimated Scope:* {estimated_scope}\\n\\n*Regulatory Notifications:* {regulatory_notifications}\\n*Deadline:* {notification_deadline}\\n\\n*Reporter:* {reporter_name} ({reporter_email})\\n*Discovery:* {discovery_time}\\n*Attack Indicators:* {attack_indicators}\\n\\nIMMEDIATE RESPONSE REQUIRED — join incident bridge.",
      ),

      // 8. Start evidence preservation runCode
      n("8", "runCode", "Initiate Evidence Preservation Protocol", 1720, {
        runCodeDetails: {
          code: `const incidentId = variables.incident_id || '';
const affectedAssets = (variables.affected_assets || '').split(',').map(a => a.trim()).filter(Boolean);
const severity = (variables.severity_level || '');
let preservationActions = [];
preservationActions.push('Snapshot all affected system logs from ' + variables.discovery_time + ' onwards');
preservationActions.push('Preserve network flow data for affected segments');
preservationActions.push('Capture current state of affected assets: ' + affectedAssets.join(', '));
preservationActions.push('Lock audit trail records for incident timeline');
preservationActions.push('Preserve authentication and access logs');
if (severity.includes('P1') || severity.includes('Critical')) {
  preservationActions.push('Engage forensic imaging for critical assets');
  preservationActions.push('Capture memory dumps of affected servers');
  preservationActions.push('Preserve email communications related to incident');
}
output.preservation_actions = preservationActions.join('; ');
output.preservation_count = preservationActions.length;
output.chain_of_custody_initiated = 'yes';
output.preservation_timestamp = new Date().toISOString();
output.forensic_readiness = severity.includes('P1') || severity.includes('Critical') ? 'full_forensic' : 'standard_preservation';`,
          outputVariables: [
            { name: "preservation_actions", type: "string" },
            { name: "preservation_count", type: "number" },
            { name: "chain_of_custody_initiated", type: "string" },
            { name: "preservation_timestamp", type: "string" },
            { name: "forensic_readiness", type: "string" },
          ],
        },
      }),

      // 9. Log immutable incident record Sheets (output_2 = lower severity, just log)
      sheetsNode(
        "9",
        "Log Immutable Security Incident Record",
        1960,
        "SecurityIncidents",
        `[["${new Date().toISOString().split("T")[0]}", "{incident_id}", "{ticket_id}", "{incident_title}", "{incident_type}", "{threat_category}", "{severity_level}", "{severity_justification}", "{affected_assets}", "{data_exposure_risk}", "{business_impact}", "{containment_priority}", "{regulatory_notifications}", "{notification_deadline}", "{preservation_count}", "{forensic_readiness}", "{reporter_name}", "{discovery_time}", "{incident_source}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
      e("6", "9", "output_2"),
    ],
  },
};
